import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { jsPDF } from "jspdf";
import { AuthProvider, useAuth } from './AuthContext';
import Login from './Login';
import SuperAdminLogin from './SuperAdminLogin';
import SuperAdminDashboard from './SuperAdminDashboard';
import Header from './Header';
import UserManagement from './UserManagement';
import ProtectedRoute from './ProtectedRoute';
import ConnectionTest from './ConnectionTest';
import RegistrationForm from './RegistrationForm';
import HomePage from './HomePage';
import MarketingLayout from './marketing/MarketingLayout';
import MarketingHome from './marketing/pages/Home';
import About from './marketing/pages/About';
import Packages from './marketing/pages/Packages';
import Contact from './marketing/pages/Contact';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import ChangePassword from './ChangePassword';
import OrganizationFeedback from './OrganizationFeedback';
import OrganizationFeedbackDetails from './OrganizationFeedbackDetails';
import OrganizationFeedbackSuccess from './OrganizationFeedbackSuccess';
import OrganizationEvaluationTab from './OrganizationEvaluationTab';
import DepartmentFeedback from './DepartmentFeedback';
import ServiceEvaluation from './ServiceEvaluation';
import ServicesTab from './admin/ServicesTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Rating scale mapping
const RATING_SCALE = {
  1: "Worst",
  2: "Bad", 
  3: "Good",
  4: "Very Good",
  5: "Excellent"
};

const RATING_COLORS = {
  1: "bg-red-500",
  2: "bg-orange-500",
  3: "bg-yellow-500",
  4: "bg-blue-500",
  5: "bg-green-500"
};

// Multi-language support
const SUPPORTED_LANGUAGES = {
  'English': 'English',
  'Arabic': 'العربية'
};

// Google Maps Geofence Component
const GeofenceMap = ({ polygons, onPolygonsChange, isDrawing, setIsDrawing }) => {
  const mapRef = React.useRef(null);
  const mapInstanceRef = React.useRef(null);
  const drawingManagerRef = React.useRef(null);
  const drawnPolygonsRef = React.useRef([]);
  const [mapLoaded, setMapLoaded] = React.useState(false);
  const [mapError, setMapError] = React.useState(null);

  React.useEffect(() => {
    // Add a small delay to ensure DOM is ready
    const initTimer = setTimeout(() => {
      if (mapRef.current && window.google && window.google.maps && window.google.maps.drawing) {
        initializeMap();
      } else {
        // Wait for Google Maps to load
        let attempts = 0;
        const checkGoogleMaps = () => {
          attempts++;
          if (mapRef.current && window.google && window.google.maps && window.google.maps.drawing) {
            initializeMap();
          } else if (attempts > 100) { // 10 seconds timeout
            setMapError('Failed to load Google Maps. Please refresh the page.');
          } else {
            setTimeout(checkGoogleMaps, 100);
          }
        };
        checkGoogleMaps();
      }
    }, 100);

    return () => clearTimeout(initTimer);
  }, []);

  const initializeMap = () => {
    try {
      if (mapInstanceRef.current) {
        return; // Already initialized
      }

      const map = new window.google.maps.Map(mapRef.current, {
        center: { lat: 37.7749, lng: -122.4194 }, // Default to San Francisco
        zoom: 13,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });

      mapInstanceRef.current = map;

      // Initialize drawing manager
      const drawingManager = new window.google.maps.drawing.DrawingManager({
        drawingMode: null,
        drawingControl: true,
        drawingControlOptions: {
          position: window.google.maps.ControlPosition.TOP_CENTER,
          drawingModes: [window.google.maps.drawing.OverlayType.POLYGON],
        },
        polygonOptions: {
          strokeColor: '#FF0000',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: '#FF0000',
          fillOpacity: 0.35,
          editable: true,
        },
      });

      drawingManagerRef.current = drawingManager;
      drawingManager.setMap(map);

      // Handle polygon complete
      drawingManager.addListener('polygoncomplete', (polygon) => {
        drawnPolygonsRef.current.push(polygon);
        
        // Get polygon coordinates
        const path = polygon.getPath();
        const coordinates = [];
        path.forEach((latLng) => {
          coordinates.push([latLng.lat(), latLng.lng()]);
        });
        
        // Update parent component
        const newPolygons = [...polygons, coordinates];
        onPolygonsChange(newPolygons);
        
        // Stop drawing mode
        drawingManager.setDrawingMode(null);
        setIsDrawing(false);
      });

      // Load existing polygons
      if (polygons && polygons.length > 0) {
        polygons.forEach((coordinates) => {
          const polygonPath = coordinates.map(coord => ({ lat: coord[0], lng: coord[1] }));
          
          const polygon = new window.google.maps.Polygon({
            paths: polygonPath,
            strokeColor: '#FF0000',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: '#FF0000',
            fillOpacity: 0.35,
            editable: true,
          });
          
          polygon.setMap(map);
          drawnPolygonsRef.current.push(polygon);
        });
      }

      setMapLoaded(true);
    } catch (error) {
      console.error('Error initializing map:', error);
      setMapError('Error initializing Google Maps. Please refresh the page.');
    }
  };

  const handleStartDrawing = () => {
    if (drawingManagerRef.current) {
      drawingManagerRef.current.setDrawingMode(window.google.maps.drawing.OverlayType.POLYGON);
      setIsDrawing(true);
    }
  };

  const handleClearPolygons = () => {
    // Clear all polygons from map
    drawnPolygonsRef.current.forEach(polygon => {
      polygon.setMap(null);
    });
    drawnPolygonsRef.current = [];
    
    // Update parent component
    onPolygonsChange([]);
  };

  if (mapError) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        <p className="font-bold">Map Error</p>
        <p>{mapError}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-lg font-medium">Geofence Areas</h4>
        <div className="space-x-2">
          {!isDrawing && (
            <button
              onClick={handleStartDrawing}
              className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 text-sm"
            >
              Draw New Area
            </button>
          )}
          <button
            onClick={handleClearPolygons}
            className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 text-sm"
          >
            Clear All
          </button>
        </div>
      </div>
      
      <div className="bg-gray-100 rounded-lg overflow-hidden">
        <div 
          ref={mapRef}
          className="w-full h-96"
          style={{ minHeight: '400px' }}
        />
      </div>
      
      {isDrawing && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
          <p className="font-bold">Drawing Mode Active</p>
          <p>Click on the map to create polygon points. Drawing will complete automatically when you finish.</p>
        </div>
      )}
      
      <div className="text-sm text-gray-600">
        <p>• Use the "Draw New Area" button to create geofence boundaries</p>
        <p>• Click points on the map to define the area</p>
        <p>• Polygons are automatically saved when drawing is complete</p>
      </div>
    </div>
  );
};

// Main Admin Dashboard Component
const AdminDashboard = () => {
  const { user } = useAuth();
  // Set default tab based on user type: 'tenants' for Super Admin, 'dashboard' for Organization users
  const [activeTab, setActiveTab] = useState(user?.organization_id === null ? 'tenants' : 'dashboard');
  // Set default settings tab based on user type: 'global' for Super Admin, 'form' for Organization admin  
  const [settingsTab, setSettingsTab] = useState(user?.organization_id === null ? 'global' : 'form');
  const [organizationTab, setOrganizationTab] = useState('account'); // Organization tab state
  const [sidebarOpen, setSidebarOpen] = useState(true); // New state for sidebar toggle
  const [stats, setStats] = useState({});
  const [employees, setEmployees] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [filteredFeedbacks, setFilteredFeedbacks] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [systemSettings, setSystemSettings] = useState(null);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showAddDepartment, setShowAddDepartment] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkUploadResult, setBulkUploadResult] = useState(null);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);
  const [viewMoreData, setViewMoreData] = useState({ type: '', title: '', data: [] });
  const [isDrawing, setIsDrawing] = useState(false);
  
  // Tenant management states (Super Admin only)
  const [showAddTenant, setShowAddTenant] = useState(false);
  const [tenants, setTenants] = useState([]);
  
  // Registration requests states (Super Admin only)
  const [registrationRequests, setRegistrationRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestFilter, setRequestFilter] = useState('pending'); // all, pending, approved, rejected
  
  // Tenant details states (Super Admin only)
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showTenantDetailsModal, setShowTenantDetailsModal] = useState(false);
  const [tenantDetails, setTenantDetails] = useState(null);
  const [tenantFeedbackCount, setTenantFeedbackCount] = useState(0);
  
  // Tenant filtering and sorting states (Super Admin only)
  const [tenantSortBy, setTenantSortBy] = useState('created_at'); // created_at, name, industry, evaluation_count
  const [tenantSortOrder, setTenantSortOrder] = useState('desc'); // asc, desc
  const [tenantFilterIndustry, setTenantFilterIndustry] = useState(''); // filter by industry
  const [tenantFilterStatus, setTenantFilterStatus] = useState('all'); // all, active, inactive
  const [tenantsWithCounts, setTenantsWithCounts] = useState([]); // tenants with evaluation counts
  
  // Password change state
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Global settings state (Super Admin only)
  const [globalSettings, setGlobalSettings] = useState({
    login_page_title: '',
    login_page_subtitle: '',
    super_admin_login_text: '',
    tenant_login_text: '',
    tenant_login_help_text: '',
    logo: '',
    logo_position: 'center',
    company_name: '',
    support_email: ''
  });
  
  // Language and alignment state
  const [currentLanguage, setCurrentLanguage] = useState('English');
  
  // Helper function to get text alignment based on language
  const getTextAlignment = (language = currentLanguage) => {
    return language === 'Arabic' ? 'text-right' : 'text-left';
  };
  
  // Employee filtering and sorting state
  const [employeeFilters, setEmployeeFilters] = useState({
    department: '',
    position: '',
    search: ''
  });
  const [employeeSort, setEmployeeSort] = useState({
    field: 'name', // name, department, position
    order: 'asc' // asc, desc
  });
  
  // Form states
  const [newEmployee, setNewEmployee] = useState({
    name: '',
    department_id: '',
    position: '',
    employee_id: '',
    photo: null
  });
  
  // Edit employee states
  const [showEditEmployee, setShowEditEmployee] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState({
    id: '',
    name: '',
    department_id: '',
    position: '',
    employee_id: '',
    photo: null,
    currentPhoto: null
  });
  
  // Department filter states for employee URLs download
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  
  const [newDepartment, setNewDepartment] = useState({
    name: '',
    description: ''
  });

  const [newTenant, setNewTenant] = useState({
    name: '',
    description: '',
    contact_email: '',
    phone: '',
    industry: '',
    website: '',
    admin_name: '',
    admin_email: '',
    admin_password: ''
  });

  // Feedback filter states
  const [feedbackRatingFilter, setFeedbackRatingFilter] = useState('');
  const [feedbackFromDate, setFeedbackFromDate] = useState('');
  const [feedbackToDate, setFeedbackToDate] = useState('');
  const [feedbackFilter, setFeedbackFilter] = useState('employees'); // New filter for employee vs organization feedback
  const [feedbackDepartmentFilter, setFeedbackDepartmentFilter] = useState('');
  const [feedbackEmployeeFilter, setFeedbackEmployeeFilter] = useState('');
  const [feedbackCustomerFilter, setFeedbackCustomerFilter] = useState('');
  const [organizationFeedbacks, setOrganizationFeedbacks] = useState([]);
  
  // Fetch organization feedbacks
  const fetchOrganizationFeedbacks = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      
      const response = await axios.get(`${API}/organizations/${user.organization_id}/feedback-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Get detailed organization feedbacks
      const feedbackResponse = await axios.get(`${API}/organizations/${user.organization_id}/organization-feedback`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setOrganizationFeedbacks(feedbackResponse.data || []);
    } catch (error) {
      console.error('Error fetching organization feedbacks:', error);
      setOrganizationFeedbacks([]);
    }
  };
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedLogo, setSelectedLogo] = useState(null); // New state for logo

  useEffect(() => {
    fetchStats();
    fetchEmployees();
    fetchFeedbacks();
    fetchOrganizationFeedbacks();
    fetchDepartments();
    
    // Fetch tenants and global settings for Super Admin (organization_id is null)
    if (user?.role === 'admin' && user?.organization_id === null) {
      fetchTenants();
      fetchRegistrationRequests();
      fetchGlobalSettings();
      // Set empty systemSettings for Super Admin to prevent null errors
      setSystemSettings({});
    } else {
      // Fetch organization-specific settings for regular users
      fetchSystemSettings();
    }
  }, [user]);

  useEffect(() => {
    // Initialize filtered feedbacks when feedbacks load
    if (feedbacks.length > 0 && filteredFeedbacks.length === 0) {
      setFilteredFeedbacks(feedbacks);
    }
  }, [feedbacks]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/feedback/dashboard-stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchFeedbacks = async () => {
    try {
      const response = await axios.get(`${API}/feedback`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      setFeedbacks(response.data);
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchSystemSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSystemSettings(response.data);
      
      // Set current language based on system settings
      if (response.data.default_language) {
        setCurrentLanguage(response.data.default_language);
      }
    } catch (error) {
      console.error('Error fetching system settings:', error);
    }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/employees`, newEmployee);
      setEmployees([...employees, response.data]);
      setNewEmployee({ name: '', department_id: '', position: '', employee_id: '', photo: null });
      setSelectedPhoto(null);
      setShowAddEmployee(false);
      fetchStats(); // Refresh stats
    } catch (error) {
      console.error('Error adding employee:', error);
      alert('Error adding employee: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  // Edit employee functions
  const handleEditEmployee = (employee) => {
    setEditingEmployee({
      id: employee.id,
      name: employee.name,
      department_id: employee.department_id,
      position: employee.position,
      employee_id: employee.employee_id,
      photo: null, // New photo will be handled separately
      currentPhoto: employee.photo // Keep track of current photo
    });
    setSelectedPhoto(null); // Reset photo selection
    setShowEditEmployee(true);
  };

  const handleUpdateEmployee = async (e) => {
    e.preventDefault();
    try {
      // Prepare employee data
      const employeeData = {
        name: editingEmployee.name,
        department_id: editingEmployee.department_id,
        position: editingEmployee.position,
        employee_id: editingEmployee.employee_id
      };

      // If a new photo was selected, include it
      if (editingEmployee.photo) {
        employeeData.photo = editingEmployee.photo;
      }

      const response = await axios.put(`${API}/employees/${editingEmployee.id}`, employeeData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      // Update the employee in the list
      const updatedEmployees = employees.map(emp => 
        emp.id === editingEmployee.id ? response.data : emp
      );
      setEmployees(updatedEmployees);
      
      // Reset form and close modal
      setEditingEmployee({ id: '', name: '', department_id: '', position: '', employee_id: '', photo: null, currentPhoto: null });
      setSelectedPhoto(null);
      setShowEditEmployee(false);
      fetchStats(); // Refresh stats
    } catch (error) {
      console.error('Error updating employee:', error);
      alert('Error updating employee: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleCancelEdit = () => {
    setEditingEmployee({ id: '', name: '', department_id: '', position: '', employee_id: '', photo: null, currentPhoto: null });
    setSelectedPhoto(null);
    setShowEditEmployee(false);
  };

  const handleAddDepartment = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/departments`, newDepartment);
      setDepartments([...departments, response.data]);
      setNewDepartment({ name: '', description: '' });
      setShowAddDepartment(false);
    } catch (error) {
      console.error('Error adding department:', error);
      alert('Error adding department: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await axios.delete(`${API}/employees/${employeeId}`);
        setEmployees(employees.filter(emp => emp.id !== employeeId));
        fetchStats(); // Refresh stats
      } catch (error) {
        console.error('Error deleting employee:', error);
        alert('Error deleting employee: ' + (error.response?.data?.detail || 'Unknown error'));
      }
    }
  };

  const generateQRCode = async (employeeId) => {
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/qr`);
      const qrData = response.data;
      
      // Create and download QR code as PNG
      const link = document.createElement('a');
      link.href = qrData.qr_code; // Backend already returns "data:image/png;base64,..." format
      link.download = `qr-${qrData.employee_name}.png`;
      link.click();
    } catch (error) {
      console.error('Error generating QR code:', error);
      alert('Error generating QR code: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const testEmployeeLink = (employeeId) => {
    // Open the feedback form for this employee in a new tab
    const feedbackUrl = `${window.location.origin}/feedback/${employeeId}`;
    window.open(feedbackUrl, '_blank');
  };

  // Tenant management functions (Super Admin only)
  const fetchTenants = async () => {
    try {
      const response = await axios.get(`${API}/organizations`);
      const organizations = response.data;
      
      // Fetch evaluation counts for each organization
      const organizationsWithCounts = await Promise.all(
        organizations.map(async (org) => {
          try {
            const countResponse = await axios.get(`${API}/organizations/${org.id}/feedbacks/count`);
            return {
              ...org,
              evaluation_count: countResponse.data.count || 0
            };
          } catch (error) {
            console.error(`Error fetching count for ${org.name}:`, error);
            return {
              ...org,
              evaluation_count: 0
            };
          }
        })
      );
      
      setTenants(organizations); // Keep original for compatibility
      setTenantsWithCounts(organizationsWithCounts); // New state with counts
      
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  const handleAddTenant = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/organizations`, newTenant);
      setTenants([...tenants, response.data]);
      setNewTenant({
        name: '',
        description: '',
        contact_email: '',
        phone: '',
        industry: '',
        website: '',
        admin_name: '',
        admin_email: '',
        admin_password: ''
      });
      setShowAddTenant(false);
      alert('Tenant created successfully!');
    } catch (error) {
      console.error('Error adding tenant:', error);
      alert('Error adding tenant: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleToggleTenantStatus = async (tenantId, newStatus) => {
    try {
      const response = await axios.put(`${API}/organizations/${tenantId}`, {
        is_active: newStatus
      });
      setTenants(tenants.map(tenant => 
        tenant.id === tenantId ? { ...tenant, is_active: newStatus } : tenant
      ));
      alert(`Tenant ${newStatus ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      console.error('Error updating tenant status:', error);
      alert('Error updating tenant status: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleDeleteTenant = async (tenantId) => {
    if (window.confirm('Are you sure you want to delete this tenant? This action cannot be undone and will remove all associated data.')) {
      try {
        await axios.delete(`${API}/organizations/${tenantId}`);
        setTenants(tenants.filter(tenant => tenant.id !== tenantId));
        alert('Tenant deleted successfully!');
      } catch (error) {
        console.error('Error deleting tenant:', error);
        alert('Error deleting tenant: ' + (error.response?.data?.detail || 'Unknown error'));
      }
    }
  };

  // Tenant Details functions (Super Admin only)
  const fetchTenantDetails = async (tenantId) => {
    try {
      // Get the registration request details for this tenant
      const registrationResponse = await axios.get(`${API}/registration-requests?status=approved`);
      const approvedRequests = registrationResponse.data;
      const tenantRegistration = approvedRequests.find(req => req.generated_organization_id === tenantId);
      
      // Get the current organization details
      const orgResponse = await axios.get(`${API}/organizations`);
      const organizations = orgResponse.data;
      const tenantOrg = organizations.find(org => org.id === tenantId);
      
      // Get feedback count for this tenant
      await fetchTenantFeedbackCount(tenantId);
      
      // Combine registration details with current org info
      setTenantDetails({
        ...tenantRegistration,
        current_organization: tenantOrg
      });
      
    } catch (error) {
      console.error('Error fetching tenant details:', error);
      setTenantDetails(null);
    }
  };

  const fetchTenantFeedbackCount = async (tenantId) => {
    try {
      // Create a temporary token for this organization to get feedback count
      // We'll get all feedbacks and count the ones for this organization
      const response = await axios.get(`${API}/organizations/${tenantId}/feedbacks/count`);
      setTenantFeedbackCount(response.data.count || 0);
    } catch (error) {
      console.error('Error fetching tenant feedback count:', error);
      // If endpoint doesn't exist, we'll try alternative approach
      try {
        const response = await axios.get(`${API}/organizations`);
        const organizations = response.data;
        const tenant = organizations.find(org => org.id === tenantId);
        
        if (tenant) {
          // For now, set to 0 - we'll implement proper counting
          setTenantFeedbackCount(0);
        }
      } catch (altError) {
        console.error('Error in alternative feedback count:', altError);
        setTenantFeedbackCount(0);
      }
    }
  };

  const handleViewTenantDetails = async (tenant) => {
    setSelectedTenant(tenant);
    setShowTenantDetailsModal(true);
    await fetchTenantDetails(tenant.id);
  };

  // Tenant sorting and filtering functions
  const getFilteredAndSortedTenants = () => {
    let filteredTenants = [...tenantsWithCounts];
    
    // Apply industry filter
    if (tenantFilterIndustry && tenantFilterIndustry !== 'all') {
      filteredTenants = filteredTenants.filter(tenant => 
        tenant.industry && tenant.industry.toLowerCase() === tenantFilterIndustry.toLowerCase()
      );
    }
    
    // Apply status filter
    if (tenantFilterStatus !== 'all') {
      filteredTenants = filteredTenants.filter(tenant => 
        tenantFilterStatus === 'active' ? tenant.is_active : !tenant.is_active
      );
    }
    
    // Apply sorting
    filteredTenants.sort((a, b) => {
      let aValue, bValue;
      
      switch (tenantSortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'industry':
          aValue = (a.industry || '').toLowerCase();
          bValue = (b.industry || '').toLowerCase();
          break;
        case 'evaluation_count':
          aValue = a.evaluation_count || 0;
          bValue = b.evaluation_count || 0;
          break;
        case 'created_at':
        default:
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
      }
      
      if (tenantSortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
    
    return filteredTenants;
  };

  const getUniqueIndustries = () => {
    const industries = tenantsWithCounts
      .map(tenant => tenant.industry)
      .filter(industry => industry && industry.trim() !== '')
      .filter((industry, index, self) => self.indexOf(industry) === index);
    return industries.sort();
  };

  // Registration Requests functions (Super Admin only)
  const fetchRegistrationRequests = async () => {
    try {
      const response = await axios.get(`${API}/registration-requests?status=${requestFilter}`);
      setRegistrationRequests(response.data);
    } catch (error) {
      console.error('Error fetching registration requests:', error);
    }
  };

  const handleApproveRequest = async (requestId, approvalNotes = '') => {
    try {
      const response = await axios.put(`${API}/registration-requests/${requestId}/approve`, {
        status: 'approved',
        approval_notes: approvalNotes
      });
      
      // Refresh the requests list
      await fetchRegistrationRequests();
      // Also refresh tenants list to show the new organization
      await fetchTenants();
      
      alert(`Registration request approved successfully!\n\nOrganization: ${response.data.organization_slug}\nAdmin Email: ${response.data.admin_email}\nAdmin Password: ${response.data.admin_password}`);
      setShowRequestModal(false);
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error approving request:', error);
      alert('Error approving request: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleRejectRequest = async (requestId, rejectionReason = '') => {
    try {
      await axios.put(`${API}/registration-requests/${requestId}/approve`, {
        status: 'rejected',
        rejection_reason: rejectionReason
      });
      
      // Refresh the requests list
      await fetchRegistrationRequests();
      
      alert('Registration request rejected successfully!');
      setShowRequestModal(false);
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('Error rejecting request: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (window.confirm('Are you sure you want to delete this registration request?')) {
      try {
        await axios.delete(`${API}/registration-requests/${requestId}`);
        await fetchRegistrationRequests();
        alert('Registration request deleted successfully!');
      } catch (error) {
        console.error('Error deleting request:', error);
        alert('Error deleting request: ' + (error.response?.data?.detail || 'Unknown error'));
      }
    }
  };

  // Re-fetch registration requests when filter changes
  useEffect(() => {
    if (user?.role === 'admin' && user?.organization_id === null && requestFilter) {
      fetchRegistrationRequests();
    }
  }, [requestFilter]);

  // Global settings functions (Super Admin only)
  const fetchGlobalSettings = async () => {
    try {
      const response = await axios.get(`${API}/global-settings`);
      setGlobalSettings(response.data);
    } catch (error) {
      console.error('Error fetching global settings:', error);
    }
  };

  const updateGlobalSettings = async (updates) => {
    try {
      const response = await axios.put(`${API}/global-settings`, updates);
      setGlobalSettings(response.data);
      alert('Global settings updated successfully!');
    } catch (error) {
      console.error('Error updating global settings:', error);
      alert('Error updating global settings: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleGlobalLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Logo = e.target.result;
        await updateGlobalSettings({ logo: base64Logo });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOrganizationLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('File size must be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64Logo = e.target.result;
          const token = localStorage.getItem('authToken');
          
          // Update the organization's logo via the organization API
          const response = await axios.put(`${API}/organizations/${user.organization_id}`, 
            { logo: base64Logo }, 
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          
          // Update the system settings to reflect the change
          setSystemSettings(prev => ({ ...prev, logo: base64Logo }));
          alert('Organization logo updated successfully!');
          
        } catch (error) {
          console.error('Error updating organization logo:', error);
          alert('Error updating organization logo: ' + (error.response?.data?.detail || 'Unknown error'));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('File size must be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const photoDataUrl = e.target.result;
        setSelectedPhoto(photoDataUrl);
        setNewEmployee({...newEmployee, photo: photoDataUrl});
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit for logos
        alert('Logo file size must be less than 2MB');
        return;
      }
      
      // Check if it's an image file
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file (PNG, JPG, SVG, etc.)');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const logoDataUrl = e.target.result;
        setSelectedLogo(logoDataUrl);
        // Update system settings with new logo
        updateSystemSettings({logo: logoDataUrl});
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setSelectedLogo(null);
    updateSystemSettings({logo: null});
  };

  const downloadCSVTemplate = async () => {
    try {
      const response = await axios.get(`${API}/csv-template`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employee_template.csv');
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV template:', error);
      alert('Error downloading CSV template: ' + (error.response?.data?.detail || 'Please try again'));
    }
  };

  const downloadDepartmentUrls = async () => {
    try {
      // Get selected department IDs for filtering
      const selectedDepts = selectedDepartments.join(',');
      
      const response = await axios.get(`${API}/employee-feedback-urls`, {
        params: selectedDepts ? { department_ids: selectedDepts } : {},
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employee_feedback_urls.csv');
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading employee URLs:', error);
      alert('Error downloading employee URLs: ' + (error.response?.data?.detail || 'Please try again'));
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/employees/bulk-upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      setBulkUploadResult(response.data);
      setShowBulkUpload(true);
      
      // Show success message
      const result = response.data;
      alert(`CSV Upload Successful!\n\nTotal rows: ${result.total_rows}\nSuccessful: ${result.successful}\nFailed: ${result.failed}\n\n${result.successful} employees have been imported successfully!`);
      
      // Refresh employees and stats
      fetchEmployees();
      fetchStats();
    } catch (error) {
      console.error('Error uploading CSV:', error);
      alert('Error uploading CSV: ' + (error.response?.data?.detail || 'Unknown error'));
    }
    
    // Reset file input
    e.target.value = '';
  };

  const applyFeedbackFilters = () => {
    let filtered = [...feedbacks];
    
    // Rating filter
    if (feedbackRatingFilter) {
      filtered = filtered.filter(feedback => feedback.rating === parseInt(feedbackRatingFilter));
    }
    
    // Date range filter
    if (feedbackFromDate || feedbackToDate) {
      filtered = filtered.filter(feedback => {
        const feedbackDate = new Date(feedback.created_at);
        const fromDate = feedbackFromDate ? new Date(feedbackFromDate) : null;
        const toDate = feedbackToDate ? new Date(feedbackToDate + 'T23:59:59') : null;
        
        if (fromDate && feedbackDate < fromDate) return false;
        if (toDate && feedbackDate > toDate) return false;
        
        return true;
      });
    }
    
    // Department filter
    if (feedbackDepartmentFilter) {
      filtered = filtered.filter(feedback => {
        const employee = employees.find(emp => emp.id === feedback.employee_id);
        return employee && (employee.department_name === feedbackDepartmentFilter || employee.department === feedbackDepartmentFilter);
      });
    }
    
    // Employee filter
    if (feedbackEmployeeFilter) {
      filtered = filtered.filter(feedback => feedback.employee_id === feedbackEmployeeFilter);
    }
    
    // Customer name filter
    if (feedbackCustomerFilter) {
      filtered = filtered.filter(feedback => 
        feedback.customer_name && feedback.customer_name.toLowerCase().includes(feedbackCustomerFilter.toLowerCase())
      );
    }
    
    setFilteredFeedbacks(filtered);
  };

  const handleFeedbackFilter = () => {
    applyFeedbackFilters();
  };

  const handleClearFilters = () => {
    setFeedbackRatingFilter('');
    setFeedbackFromDate('');
    setFeedbackToDate('');
    setFeedbackDepartmentFilter('');
    setFeedbackEmployeeFilter('');
    setFeedbackCustomerFilter('');
    setFilteredFeedbacks(feedbacks);
  };

  const setPresetDateRange = (preset) => {
    const today = new Date();
    let fromDate, toDate;
    
    switch (preset) {
      case 'today':
        fromDate = today.toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        fromDate = yesterday.toISOString().split('T')[0];
        toDate = yesterday.toISOString().split('T')[0];
        break;
      case 'last7days':
        const last7Days = new Date(today);
        last7Days.setDate(last7Days.getDate() - 7);
        fromDate = last7Days.toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      case 'last30days':
        const last30Days = new Date(today);
        last30Days.setDate(last30Days.getDate() - 30);
        fromDate = last30Days.toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      case 'thismonth':
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        fromDate = firstDay.toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      default:
        return;
    }
    
    setFeedbackFromDate(fromDate);
    setFeedbackToDate(toDate);
  };

  const downloadFeedbackCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (feedbackRatingFilter) params.append('rating_filter', feedbackRatingFilter);
      if (feedbackFromDate) params.append('from_date', feedbackFromDate);
      if (feedbackToDate) params.append('to_date', feedbackToDate);
      
      const response = await axios.get(`${API}/feedback/download-csv?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'feedback_export.csv');
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading feedback CSV:', error);
      alert('Error downloading feedback CSV');
    }
  };

  const updateSystemSettings = async (updates) => {
    try {
      const response = await axios.put(`${API}/settings`, updates);
      setSystemSettings(response.data);
    } catch (error) {
      console.error('Error updating system settings:', error);
      alert('Error updating system settings: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleViewMore = (type, title, data) => {
    setViewMoreData({ type, title, data });
    setShowViewMoreModal(true);
  };

  const handlePasswordChange = async () => {
    try {
      const response = await axios.post(`${API}/auth/password-change`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.status === 200) {
        alert('Password changed successfully!');
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Error changing password: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  // Employee filtering and sorting functions
  const getFilteredAndSortedEmployees = () => {
    let filtered = [...employees];
    
    // Apply filters
    if (employeeFilters.department) {
      filtered = filtered.filter(employee => 
        (employee.department_name || employee.department) === employeeFilters.department
      );
    }
    
    if (employeeFilters.position) {
      filtered = filtered.filter(employee => 
        employee.position.toLowerCase().includes(employeeFilters.position.toLowerCase())
      );
    }
    
    if (employeeFilters.search) {
      filtered = filtered.filter(employee => 
        employee.name.toLowerCase().includes(employeeFilters.search.toLowerCase()) ||
        employee.employee_id.toLowerCase().includes(employeeFilters.search.toLowerCase())
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (employeeSort.field) {
        case 'department':
          aValue = (a.department_name || a.department || '').toLowerCase();
          bValue = (b.department_name || b.department || '').toLowerCase();
          break;
        case 'position':
          aValue = (a.position || '').toLowerCase();
          bValue = (b.position || '').toLowerCase();
          break;
        case 'name':
        default:
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
      }
      
      if (employeeSort.order === 'desc') {
        return bValue.localeCompare(aValue);
      } else {
        return aValue.localeCompare(bValue);
      }
    });
    
    return filtered;
  };

  const getUniquePositions = () => {
    const positions = employees.map(emp => emp.position).filter(Boolean);
    return [...new Set(positions)].sort();
  };

  const handleEmployeeFilterChange = (filterType, value) => {
    setEmployeeFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleEmployeeSortChange = (field) => {
    setEmployeeSort(prev => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc'
    }));
  };

  // PDF Generation Function for Employee Cards
  const generateEmployeeCard = async (employee) => {
    try {
      // Create PDF with specified dimensions (551x867 pixels)
      // Convert pixels to mm (approximately 1 pixel = 0.264583 mm)
      const widthMm = 551 * 0.264583; // ~145.8 mm
      const heightMm = 867 * 0.264583; // ~229.3 mm
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [widthMm, heightMm]
      });

      // Background color
      pdf.setFillColor(245, 247, 250); // Light gray background
      pdf.rect(0, 0, widthMm, heightMm, 'F');

      // Header section with company logo
      if (systemSettings?.logo) {
        try {
          // Add company logo at the top
          pdf.addImage(systemSettings.logo, 'JPEG', 10, 10, 30, 30);
        } catch (error) {
          console.warn('Could not add company logo to PDF:', error);
        }
      }

      // Company name/title
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(31, 41, 55); // Dark gray
      const companyName = user?.organization_name || 'Rate My Service';
      pdf.text(companyName, widthMm/2, 25, { align: 'center' });

      // Card title
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(75, 85, 99); // Medium gray
      pdf.text('Employee ID Card', widthMm/2, 35, { align: 'center' });

      // Employee photo section
      let photoY = 50;
      if (employee.photo) {
        try {
          // Add employee photo (centered, square format)
          const photoSize = 40;
          const photoX = (widthMm - photoSize) / 2;
          pdf.addImage(employee.photo, 'JPEG', photoX, photoY, photoSize, photoSize);
          photoY += photoSize + 10;
        } catch (error) {
          console.warn('Could not add employee photo to PDF:', error);
          // Add placeholder for photo
          pdf.setFillColor(229, 231, 235);
          pdf.rect((widthMm - 40) / 2, photoY, 40, 40, 'F');
          pdf.setFontSize(10);
          pdf.setTextColor(107, 114, 128);
          pdf.text('No Photo', widthMm/2, photoY + 22, { align: 'center' });
          photoY += 50;
        }
      } else {
        // Add placeholder for photo
        pdf.setFillColor(229, 231, 235);
        pdf.rect((widthMm - 40) / 2, photoY, 40, 40, 'F');
        pdf.setFontSize(10);
        pdf.setTextColor(107, 114, 128);
        pdf.text('No Photo', widthMm/2, photoY + 22, { align: 'center' });
        photoY += 50;
      }

      // Employee information
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(31, 41, 55);
      pdf.text(employee.name, widthMm/2, photoY, { align: 'center' });

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(75, 85, 99);
      pdf.text(employee.position, widthMm/2, photoY + 8, { align: 'center' });

      pdf.setFontSize(12);
      pdf.setTextColor(107, 114, 128);
      const department = employee.department_name || employee.department || 'No Department';
      pdf.text(department, widthMm/2, photoY + 16, { align: 'center' });

      // Employee ID
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(75, 85, 99);
      pdf.text(`Employee ID: ${employee.employee_id}`, widthMm/2, photoY + 26, { align: 'center' });

      // QR Code section
      const qrY = photoY + 40;
      
      try {
        // Generate QR code using the existing backend endpoint
        const qrResponse = await axios.get(`${API}/employees/${employee.id}/qr`);
        const qrData = qrResponse.data;
        
        if (qrData.qr_code) {
          // Add the actual QR code to PDF
          const qrSize = 35;
          const qrX = (widthMm - qrSize) / 2;
          pdf.addImage(qrData.qr_code, 'PNG', qrX, qrY, qrSize, qrSize);
        } else {
          throw new Error('QR code data not available');
        }
      } catch (error) {
        console.warn('Could not add QR code to PDF, using placeholder:', error);
        // Add QR code placeholder
        pdf.setFillColor(255, 255, 255);
        pdf.rect((widthMm - 35) / 2, qrY, 35, 35, 'F');
        pdf.setDrawColor(200, 200, 200);
        pdf.rect((widthMm - 35) / 2, qrY, 35, 35, 'S');
        
        // QR Code placeholder text
        pdf.setFontSize(8);
        pdf.setTextColor(107, 114, 128);
        pdf.text('QR CODE', widthMm/2, qrY + 18, { align: 'center' });
      }
      
      // Instructions
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(75, 85, 99);
      pdf.text('Scan to rate this employee', widthMm/2, qrY + 45, { align: 'center' });
      
      // URL at the bottom
      const qrUrl = `${window.location.origin}/feedback/${employee.id}`;
      pdf.setFontSize(8);
      pdf.setTextColor(107, 114, 128);
      pdf.text(qrUrl, widthMm/2, qrY + 55, { align: 'center' });

      // Footer
      pdf.setFontSize(8);
      pdf.setTextColor(156, 163, 175);
      pdf.text('Generated by Rate My Service Platform', widthMm/2, heightMm - 10, { align: 'center' });

      // Save the PDF
      const fileName = `${employee.name.replace(/\s+/g, '_')}_ID_Card.pdf`;
      pdf.save(fileName);
      
      return true;
    } catch (error) {
      console.error('Error generating employee card PDF:', error);
      alert('Error generating PDF. Please try again.');
      return false;
    }
  };

  // Calculate dashboard stats from API data or fallback to local calculation
  const totalFeedbacks = stats?.total_system_reviews || feedbacks.length;
  const averageRating = stats?.total_system_reviews > 0 
    ? (stats?.most_reviewed_staff?.reduce((sum, staff) => {
        return sum + (staff.average_rating * staff.total_reviews);
      }, 0) / stats?.total_system_reviews || 0).toFixed(1)
    : (feedbacks.length > 0 
        ? (feedbacks.reduce((sum, f) => sum + f.rating, 0) / feedbacks.length).toFixed(1)
        : 0);

  const ratingDistribution = feedbacks.reduce((acc, feedback) => {
    acc[feedback.rating] = (acc[feedback.rating] || 0) + 1;
    return acc;
  }, {});

  const goodReviews = stats?.good_reviews || feedbacks.filter(f => f.rating >= 4).length;
  const badReviews = stats?.poor_reviews || feedbacks.filter(f => f.rating <= 3).length;

  // Calculate staff performance from API data or fallback to local calculation
  const staffPerformance = stats?.most_reviewed_staff?.map(staff => ({
    id: staff.employee_id,
    name: staff.name,
    department_name: staff.department,
    total_reviews: staff.total_reviews,
    average_rating: staff.average_rating
  })) || employees.map(emp => {
    const empFeedbacks = feedbacks.filter(f => f.employee_id === emp.id);
    return {
      ...emp,
      total_reviews: empFeedbacks.length,
      average_rating: empFeedbacks.length > 0 
        ? empFeedbacks.reduce((sum, f) => sum + f.rating, 0) / empFeedbacks.length
        : 0
    };
  });

  const mostReviewed = staffPerformance.sort((a, b) => b.total_reviews - a.total_reviews).slice(0, 5);
  const highestRated = staffPerformance.filter(s => s.total_reviews > 0).sort((a, b) => b.average_rating - a.average_rating).slice(0, 5);
  const lowestRated = staffPerformance.filter(s => s.total_reviews > 0).sort((a, b) => a.average_rating - b.average_rating).slice(0, 5);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="flex">
        {/* Sidebar */}
        <div className={`${sidebarOpen ? 'w-64' : 'w-16'} bg-white shadow-lg transition-all duration-300 ease-in-out min-h-screen`}>
          {/* Sidebar Header */}
          <div className="flex items-center justify-between p-4 border-b">
            {sidebarOpen && (
              <h2 className="text-lg font-semibold text-gray-800">Navigation</h2>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-md hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {sidebarOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                )}
              </svg>
            </button>
          </div>

          {/* Navigation Items */}
          <nav className="mt-4">
            <ul className="space-y-2 px-3">
              {/* Dashboard (Organization Users Only) */}
              {user?.organization_id !== null && (
                <li>
                  <button
                    onClick={() => setActiveTab('dashboard')}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === 'dashboard'
                        ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {sidebarOpen && <span>Dashboard</span>}
                  </button>
                </li>
              )}

              {/* Users (Organization Admin Only) */}
              {user?.role === 'admin' && user?.organization_id !== null && (
                <li>
                  <button
                    onClick={() => setActiveTab('users')}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === 'users'
                        ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {sidebarOpen && <span>Users</span>}
                  </button>
                </li>
              )}

              {/* Tenants (Super Admin Only) */}
              {user?.role === 'admin' && user?.organization_id === null && (
                <li>
                  <button
                    onClick={() => setActiveTab('tenants')}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === 'tenants'
                        ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {sidebarOpen && <span>Tenants</span>}
                  </button>
                </li>
              )}

              {/* Departments (Organization Admin/Supervisor Only) */}
              {(user?.role === 'admin' || user?.role === 'supervisor') && user?.organization_id !== null && (
                <li>
                  <button
                    onClick={() => setActiveTab('departments')}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === 'departments'
                        ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {sidebarOpen && <span>Departments</span>}
                  </button>
                </li>
              )}

              {/* Employees (Organization Admin/Supervisor Only) */}
              {(user?.role === 'admin' || user?.role === 'supervisor') && user?.organization_id !== null && (
                <li>
                  <button
                    onClick={() => setActiveTab('employees')}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === 'employees'
                        ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {sidebarOpen && <span>Employees</span>}
                  </button>
                </li>
              )}

              {/* Services (Organization Admin Only) */}
              {user?.role === 'admin' && user?.organization_id !== null && (
                <li>
                  <button
                    data-testid="sidebar-services"
                    onClick={() => setActiveTab('services')}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === 'services'
                        ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {sidebarOpen && <span>Services</span>}
                  </button>
                </li>
              )}

              {/* Feedbacks (Organization Admin/Supervisor Only) */}
              {(user?.role === 'admin' || user?.role === 'supervisor') && user?.organization_id !== null && (
                <li>
                  <button
                    onClick={() => setActiveTab('feedbacks')}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === 'feedbacks'
                        ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {sidebarOpen && <span>Feedbacks</span>}
                  </button>
                </li>
              )}

              {/* Configuration (Admin Only) */}
              {user?.role === 'admin' && (
                <li>
                  <button
                    onClick={() => setActiveTab('configuration')}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === 'configuration'
                        ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {sidebarOpen && <span>Configuration</span>}
                  </button>
                </li>
              )}

              {/* Organization (Organization Admin Only) */}
              {user?.role === 'admin' && user?.organization_id !== null && (
                <li>
                  <button
                    onClick={() => setActiveTab('organization')}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === 'organization'
                        ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {sidebarOpen && <span>Organization</span>}
                  </button>
                </li>
              )}

              {/* My Subscription (Organization Admin Only) */}
              {user?.role === 'admin' && user?.organization_id !== null && (
                <li>
                  <button
                    onClick={() => setActiveTab('my-subscription')}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === 'my-subscription'
                        ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {sidebarOpen && <span>My Subscription</span>}
                  </button>
                </li>
              )}

              {/* Subscription Packages (Super Admin Only) */}
              {user?.role === 'admin' && user?.organization_id === null && (
                <li>
                  <button
                    onClick={() => setActiveTab('subscription-packages')}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === 'subscription-packages'
                        ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {sidebarOpen && <span>Subscription Packages</span>}
                  </button>
                </li>
              )}

              {/* Package Change Requests (Super Admin Only) */}
              {user?.role === 'admin' && user?.organization_id === null && (
                <li>
                  <button
                    onClick={() => setActiveTab('package-change-requests')}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === 'package-change-requests'
                        ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {sidebarOpen && <span>Package Requests</span>}
                  </button>
                </li>
              )}
            </ul>
          </nav>

          {/* Sidebar Footer */}
          {sidebarOpen && (
            <div className="mt-auto mb-4 mx-4">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">Rate My Service</p>
                <p className="text-xs text-gray-400">v1.0</p>
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6">
          {/* Dashboard Content */}
          <div className="max-w-7xl mx-auto">
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Dashboard Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-semibold">📊</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Total Reviews</p>
                    <p className="text-2xl font-bold text-gray-900">{totalFeedbacks}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 font-semibold">⭐</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Average Rating</p>
                    <p className="text-2xl font-bold text-gray-900">{averageRating}/5</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 font-semibold">👍</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Good Reviews</p>
                    <p className="text-2xl font-bold text-gray-900">{goodReviews}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                      <span className="text-red-600 font-semibold">👎</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Poor Reviews</p>
                    <p className="text-2xl font-bold text-gray-900">{badReviews}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Staff Performance Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Most Reviewed Staff</h3>
                  <button 
                    onClick={() => handleViewMore('reviews', 'Most Reviewed Staff', mostReviewed)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View More
                  </button>
                </div>
                <div className="space-y-3">
                  {mostReviewed.slice(0, 3).map((staff, index) => (
                    <div key={staff.id} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-500 mr-2">#{index + 1}</span>
                        <span className="text-sm text-gray-900">{staff.name}</span>
                      </div>
                      <span className="text-sm text-blue-600 font-medium">{staff.total_reviews} reviews</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Highest Rated Staff</h3>
                  <button 
                    onClick={() => handleViewMore('rating', 'Highest Rated Staff', highestRated)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View More
                  </button>
                </div>
                <div className="space-y-3">
                  {highestRated.slice(0, 3).map((staff, index) => (
                    <div key={staff.id} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-500 mr-2">#{index + 1}</span>
                        <span className="text-sm text-gray-900">{staff.name}</span>
                      </div>
                      <span className="text-sm text-gray-900 font-medium">{staff.average_rating.toFixed(1)}/5</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Needs Attention</h3>
                  <button 
                    onClick={() => handleViewMore('rating', 'Staff Needing Attention', lowestRated)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View More
                  </button>
                </div>
                <div className="space-y-3">
                  {lowestRated.slice(0, 3).map((staff, index) => (
                    <div key={staff.id} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-500 mr-2">#{index + 1}</span>
                        <span className="text-sm text-gray-900">{staff.name}</span>
                      </div>
                      <span className="text-sm text-gray-900 font-medium">{staff.average_rating.toFixed(1)}/5</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recent Feedback */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Feedback</h3>
              <div className="space-y-4">
                {feedbacks.slice(0, 5).map((feedback) => {
                  const employee = employees.find(emp => emp.id === feedback.employee_id);
                  return (
                    <div key={feedback.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white ${RATING_COLORS[feedback.rating]}`}>
                          {feedback.rating}/5
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-900">
                          <span className="font-medium">{feedback.customer_name}</span> rated{' '}
                          <span className="font-medium">{employee?.name || 'Unknown'}</span>
                        </p>
                        <p className="text-sm text-gray-500">{feedback.rating_text}</p>
                        {feedback.comments && (
                          <p className="text-sm text-gray-600 mt-1">"{feedback.comments}"</p>
                        )}
                      </div>
                      <div className="flex-shrink-0 text-sm text-gray-500">
                        {new Date(feedback.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* View More Modal */}
        {showViewMoreModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 mx-4 max-w-2xl w-full max-h-96 overflow-hidden">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-800">{viewMoreData.title}</h3>
                <button
                  onClick={() => setShowViewMoreModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="overflow-y-auto max-h-80">
                <div className="space-y-3">
                  {viewMoreData.data.map((staff, index) => (
                    <div key={staff.employee_id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <span className="font-medium text-gray-800">#{index + 1} {staff.name}</span>
                        <p className="text-sm text-gray-500">Employee ID: {staff.employee_id}</p>
                      </div>
                      <div className="text-right">
                        {viewMoreData.type === 'reviews' ? (
                          <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded">
                            {staff.total_reviews} Reviews
                          </span>
                        ) : (
                          <span className="bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded">
                            {staff.average_rating.toFixed(1)}/5 ⭐
                          </span>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          {staff.total_reviews} total review{staff.total_reviews !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                  
                  {viewMoreData.data.length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No staff data available</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowViewMoreModal(false)}
                  className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Other Content Sections */}
        {activeTab === 'users' && user?.role === 'admin' && (
          <ProtectedRoute requireAdmin={true}>
            <UserManagement />
          </ProtectedRoute>
        )}

        {/* Tenants Management (Super Admin Only) */}
        {activeTab === 'tenants' && user?.role === 'admin' && user?.organization_id === null && (
          <ProtectedRoute requireAdmin={true}>
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Tenant Management</h2>
                <div className="flex space-x-4">
                  <button
                    onClick={() => window.open('/register', '_blank')}
                    className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600"
                  >
                    Open Registration Form
                  </button>
                  <button
                    onClick={() => setShowAddTenant(true)}
                    className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
                  >
                    Add New Tenant
                  </button>
                </div>
              </div>

              {/* Tab Navigation */}
              <div className="bg-white rounded-lg shadow mb-6">
                <div className="border-b border-gray-200">
                  <nav className="flex">
                    <button
                      onClick={() => setRequestFilter('all')}
                      className={`px-6 py-3 text-sm font-medium border-b-2 ${
                        requestFilter === 'all'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Active Organizations
                    </button>
                    <button
                      onClick={() => setRequestFilter('pending')}
                      className={`px-6 py-3 text-sm font-medium border-b-2 ${
                        requestFilter === 'pending'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Pending Requests ({registrationRequests.filter(r => r.status === 'pending').length})
                    </button>
                    <button
                      onClick={() => setRequestFilter('approved')}
                      className={`px-6 py-3 text-sm font-medium border-b-2 ${
                        requestFilter === 'approved'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Approved Requests
                    </button>
                    <button
                      onClick={() => setRequestFilter('rejected')}
                      className={`px-6 py-3 text-sm font-medium border-b-2 ${
                        requestFilter === 'rejected'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Rejected Requests
                    </button>
                  </nav>
                </div>
              </div>

              {showAddTenant && (
                <div className="mb-6 p-4 border rounded-lg bg-gray-50">
                  <h3 className="text-lg font-medium mb-4">Add New Tenant</h3>
                  <form onSubmit={handleAddTenant} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="Organization Name"
                        value={newTenant.name}
                        onChange={(e) => setNewTenant({...newTenant, name: e.target.value})}
                        className="w-full border rounded-md px-3 py-2"
                        required
                      />
                      <input
                        type="email"
                        placeholder="Contact Email"
                        value={newTenant.contact_email}
                        onChange={(e) => setNewTenant({...newTenant, contact_email: e.target.value})}
                        className="w-full border rounded-md px-3 py-2"
                        required
                      />
                      <input
                        type="text"
                        placeholder="Phone (optional)"
                        value={newTenant.phone}
                        onChange={(e) => setNewTenant({...newTenant, phone: e.target.value})}
                        className="w-full border rounded-md px-3 py-2"
                      />
                      <input
                        type="text"
                        placeholder="Industry (optional)"
                        value={newTenant.industry}
                        onChange={(e) => setNewTenant({...newTenant, industry: e.target.value})}
                        className="w-full border rounded-md px-3 py-2"
                      />
                      <input
                        type="url"
                        placeholder="Website (optional)"
                        value={newTenant.website}
                        onChange={(e) => setNewTenant({...newTenant, website: e.target.value})}
                        className="w-full border rounded-md px-3 py-2"
                      />
                    </div>
                    <textarea
                      placeholder="Description (optional)"
                      value={newTenant.description}
                      onChange={(e) => setNewTenant({...newTenant, description: e.target.value})}
                      className="w-full border rounded-md px-3 py-2"
                      rows="3"
                    />
                    <div className="border-t pt-4">
                      <h4 className="text-md font-medium mb-2">Admin User Details</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="Admin Name"
                          value={newTenant.admin_name}
                          onChange={(e) => setNewTenant({...newTenant, admin_name: e.target.value})}
                          className="w-full border rounded-md px-3 py-2"
                          required
                        />
                        <input
                          type="email"
                          placeholder="Admin Email"
                          value={newTenant.admin_email}
                          onChange={(e) => setNewTenant({...newTenant, admin_email: e.target.value})}
                          className="w-full border rounded-md px-3 py-2"
                          required
                        />
                        <input
                          type="password"
                          placeholder="Admin Password"
                          value={newTenant.admin_password}
                          onChange={(e) => setNewTenant({...newTenant, admin_password: e.target.value})}
                          className="w-full border rounded-md px-3 py-2"
                          required
                          minLength="8"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600"
                      >
                        Create Tenant
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAddTenant(false)}
                        className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Content based on selected tab */}
              {requestFilter === 'all' && (
                <div className="bg-white rounded-lg shadow">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-medium text-gray-900 text-left">Active Organizations</h3>
                      <span className="text-sm text-gray-500">
                        {getFilteredAndSortedTenants().length} organization(s)
                      </span>
                    </div>
                    
                    {/* Filters and Sorting */}
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Sort By */}
                        <div>
                          <label className={`block text-sm font-medium text-gray-700 mb-2 ${getTextAlignment()}`}>Sort By</label>
                          <select
                            value={tenantSortBy}
                            onChange={(e) => setTenantSortBy(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          >
                            <option value="created_at">Date Registered</option>
                            <option value="name">Organization Name</option>
                            <option value="industry">Industry</option>
                            <option value="evaluation_count">Number of Evaluations</option>
                          </select>
                        </div>
                        
                        {/* Sort Order */}
                        <div>
                          <label className={`block text-sm font-medium text-gray-700 mb-2 ${getTextAlignment()}`}>Order</label>
                          <select
                            value={tenantSortOrder}
                            onChange={(e) => setTenantSortOrder(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          >
                            <option value="desc">Descending</option>
                            <option value="asc">Ascending</option>
                          </select>
                        </div>
                        
                        {/* Industry Filter */}
                        <div>
                          <label className={`block text-sm font-medium text-gray-700 mb-2 ${getTextAlignment()}`}>Filter by Industry</label>
                          <select
                            value={tenantFilterIndustry}
                            onChange={(e) => setTenantFilterIndustry(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          >
                            <option value="">All Industries</option>
                            {getUniqueIndustries().map(industry => (
                              <option key={industry} value={industry}>{industry}</option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Status Filter */}
                        <div>
                          <label className={`block text-sm font-medium text-gray-700 mb-2 ${getTextAlignment()}`}>Filter by Status</label>
                          <select
                            value={tenantFilterStatus}
                            onChange={(e) => setTenantFilterStatus(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          >
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 gap-4">
                      {getFilteredAndSortedTenants().map((tenant) => (
                        <div key={tenant.id} className="border rounded-lg p-4 hover:bg-gray-50">
                          <div className="flex justify-between items-start">
                            <div className="flex-1 text-left">
                              <div className="flex items-center space-x-3">
                                <h3 className="text-lg font-medium text-gray-900 text-left">{tenant.name}</h3>
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                  tenant.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                  {tenant.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                              <div className="mt-1 space-y-1 text-left">
                                <div className="flex flex-wrap gap-4">
                                  <p className="text-sm text-gray-600 text-left">
                                    <span className="font-medium">Slug:</span> {tenant.slug}
                                  </p>
                                  <p className="text-sm text-gray-600 text-left">
                                    <span className="font-medium">Evaluations:</span> 
                                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full ml-1">
                                      {tenant.evaluation_count || 0}
                                    </span>
                                  </p>
                                </div>
                                <p className="text-sm text-gray-600 text-left">
                                  <span className="font-medium">Contact:</span> {tenant.contact_email || 'N/A'}
                                </p>
                                {tenant.phone && (
                                  <p className="text-sm text-gray-600 text-left">
                                    <span className="font-medium">Phone:</span> {tenant.phone}
                                  </p>
                                )}
                                {tenant.industry && (
                                  <p className="text-sm text-gray-600 text-left">
                                    <span className="font-medium">Industry:</span> {tenant.industry}
                                  </p>
                                )}
                                {tenant.website && (
                                  <p className="text-sm text-gray-600 text-left">
                                    <span className="font-medium">Website:</span> 
                                    <a href={tenant.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 ml-1">
                                      {tenant.website}
                                    </a>
                                  </p>
                                )}
                                <p className="text-sm text-gray-600 text-left">
                                  <span className="font-medium">Created:</span> {new Date(tenant.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleViewTenantDetails(tenant)}
                                className="px-3 py-1 text-sm font-medium text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200"
                              >
                                View Details
                              </button>
                              <button
                                onClick={() => handleToggleTenantStatus(tenant.id, !tenant.is_active)}
                                className={`px-3 py-1 text-sm font-medium rounded-md ${
                                  tenant.is_active 
                                    ? 'text-orange-600 bg-orange-100 hover:bg-orange-200'
                                    : 'text-green-600 bg-green-100 hover:bg-green-200'
                                }`}
                              >
                                {tenant.is_active ? 'Deactivate' : 'Activate'}
                              </button>
                              <button
                                onClick={() => handleDeleteTenant(tenant.id)}
                                className="px-3 py-1 text-sm font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {getFilteredAndSortedTenants().length === 0 && (
                        <div className="text-left py-8 text-gray-500 bg-gray-50 rounded-lg px-4">
                          <p className="text-left">No organizations found matching your current filters.</p>
                          <p className="text-left text-sm mt-2">Try adjusting your search criteria or add your first organization to get started.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Registration Requests */}
              {requestFilter !== 'all' && (
                <div className="bg-white rounded-lg shadow">
                  <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4 text-left">
                      {requestFilter.charAt(0).toUpperCase() + requestFilter.slice(1)} Registration Requests
                    </h3>
                    <div className="space-y-4">
                      {registrationRequests.filter(req => req.status === requestFilter).map((request) => (
                        <div key={request.id} className="border rounded-lg p-4 hover:bg-gray-50">
                          <div className="flex justify-between items-start">
                            <div className="flex-1 text-left">
                              <div className="flex items-center space-x-3 mb-2">
                                <h4 className="text-lg font-medium text-gray-900 text-left">{request.organization_name}</h4>
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                  request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  request.status === 'approved' ? 'bg-green-100 text-green-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                                </span>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600 text-left">
                                <p className="text-left"><span className="font-medium">Industry:</span> {request.industry}</p>
                                <p className="text-left"><span className="font-medium">Team Size:</span> {request.team_size}</p>
                                <p className="text-left"><span className="font-medium">Location:</span> {request.location}</p>
                                <p className="text-left"><span className="font-medium">Email:</span> {request.official_email}</p>
                                <p className="text-left"><span className="font-medium">Contact:</span> {request.contact_person_name}</p>
                                <p className="text-left"><span className="font-medium">Title:</span> {request.contact_person_title}</p>
                                <p className="text-left"><span className="font-medium">Expected Reviews:</span> {request.expected_monthly_reviews}</p>
                                <p className="text-left"><span className="font-medium">Card Requirement:</span> {request.card_requirement}</p>
                              </div>
                              <div className="mt-2 text-sm text-gray-500 text-left">
                                <p className="text-left">Submitted: {new Date(request.submitted_at).toLocaleString()}</p>
                                {request.reviewed_at && (
                                  <p className="text-left">Reviewed: {new Date(request.reviewed_at).toLocaleString()}</p>
                                )}
                              </div>
                              {request.special_requirements && (
                                <div className="mt-2 text-left">
                                  <p className="text-sm font-medium text-gray-700 text-left">Special Requirements:</p>
                                  <p className="text-sm text-gray-600 text-left">{request.special_requirements}</p>
                                </div>
                              )}
                              {request.approval_notes && (
                                <div className="mt-2 p-2 bg-green-50 rounded text-left">
                                  <p className="text-sm font-medium text-green-700 text-left">Approval Notes:</p>
                                  <p className="text-sm text-green-600 text-left">{request.approval_notes}</p>
                                </div>
                              )}
                              {request.rejection_reason && (
                                <div className="mt-2 p-2 bg-red-50 rounded text-left">
                                  <p className="text-sm font-medium text-red-700 text-left">Rejection Reason:</p>
                                  <p className="text-sm text-red-600 text-left">{request.rejection_reason}</p>
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col space-y-2">
                              {request.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => {
                                      setSelectedRequest(request);
                                      setShowRequestModal(true);
                                    }}
                                    className="px-3 py-1 text-sm font-medium text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200"
                                  >
                                    Review
                                  </button>
                                  <button
                                    onClick={() => handleApproveRequest(request.id)}
                                    className="px-3 py-1 text-sm font-medium text-green-600 bg-green-100 rounded-md hover:bg-green-200"
                                  >
                                    Quick Approve
                                  </button>
                                  <button
                                    onClick={() => handleRejectRequest(request.id, 'Request rejected by admin')}
                                    className="px-3 py-1 text-sm font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200"
                                  >
                                    Quick Reject
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => handleDeleteRequest(request.id)}
                                className="px-3 py-1 text-sm font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {registrationRequests.filter(req => req.status === requestFilter).length === 0 && (
                        <div className="text-left py-8 text-gray-500 bg-gray-50 rounded-lg px-4">
                          <p className="text-left">No {requestFilter} registration requests found.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Request Review Modal */}
              {showRequestModal && selectedRequest && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg p-6 mx-4 max-w-2xl w-full max-h-96 overflow-y-auto">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-bold text-gray-800">Review Registration Request</h3>
                      <button
                        onClick={() => {
                          setShowRequestModal(false);
                          setSelectedRequest(null);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-md font-semibold text-gray-900">{selectedRequest.organization_name}</h4>
                        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          <p><span className="font-medium">Industry:</span> {selectedRequest.industry}</p>
                          <p><span className="font-medium">Team Size:</span> {selectedRequest.team_size}</p>
                          <p><span className="font-medium">Location:</span> {selectedRequest.location}</p>
                          <p><span className="font-medium">Email:</span> {selectedRequest.official_email}</p>
                          <p><span className="font-medium">Phone:</span> {selectedRequest.phone_number}</p>
                          <p><span className="font-medium">Contact Person:</span> {selectedRequest.contact_person_name}</p>
                          <p><span className="font-medium">Title:</span> {selectedRequest.contact_person_title}</p>
                          <p><span className="font-medium">Expected Reviews:</span> {selectedRequest.expected_monthly_reviews}</p>
                          <p><span className="font-medium">Language:</span> {selectedRequest.preferred_language}</p>
                          <p><span className="font-medium">Card Requirement:</span> {selectedRequest.card_requirement}</p>
                          {selectedRequest.company_registration_number && (
                            <p><span className="font-medium">Registration #:</span> {selectedRequest.company_registration_number}</p>
                          )}
                        </div>
                        {selectedRequest.special_requirements && (
                          <div className="mt-3">
                            <p className="font-medium text-sm">Special Requirements:</p>
                            <p className="text-sm text-gray-600 mt-1">{selectedRequest.special_requirements}</p>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex space-x-4 pt-4 border-t">
                        <button
                          onClick={() => {
                            const notes = prompt('Approval notes (optional):');
                            handleApproveRequest(selectedRequest.id, notes || '');
                          }}
                          className="flex-1 bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600"
                        >
                          Approve Request
                        </button>
                        <button
                          onClick={() => {
                            const reason = prompt('Rejection reason:');
                            if (reason) {
                              handleRejectRequest(selectedRequest.id, reason);
                            }
                          }}
                          className="flex-1 bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600"
                        >
                          Reject Request
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ProtectedRoute>
        )}

        {activeTab === 'departments' && user?.role === 'admin' && (
          <ProtectedRoute requireAdmin={true}>
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Departments</h2>
                <div className="flex space-x-3">
                  <button
                    onClick={downloadDepartmentUrls}
                    className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 flex items-center space-x-2"
                    title="Download department feedback URLs as CSV"
                  >
                    <span>📊</span>
                    <span>Download URLs</span>
                  </button>
                  <button
                    onClick={() => setShowAddDepartment(true)}
                    className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
                  >
                    Add Department
                  </button>
                </div>
              </div>

              {showAddDepartment && (
                <div className="mb-6 p-4 border rounded-lg bg-gray-50">
                  <h3 className="text-lg font-medium mb-4">Add New Department</h3>
                  <form onSubmit={handleAddDepartment} className="space-y-4">
                    <input
                      type="text"
                      placeholder="Department Name"
                      value={newDepartment.name}
                      onChange={(e) => setNewDepartment({...newDepartment, name: e.target.value})}
                      className="w-full border rounded-md px-3 py-2"
                      required
                    />
                    <textarea
                      placeholder="Description (optional)"
                      value={newDepartment.description}
                      onChange={(e) => setNewDepartment({...newDepartment, description: e.target.value})}
                      className="w-full border rounded-md px-3 py-2"
                      rows="3"
                    />
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600"
                      >
                        Add Department
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAddDepartment(false)}
                        className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {departments.map((department) => {
                  const deptReviewUrl = `${window.location.origin}/department-feedback/${department.id}`;
                  return (
                  <div key={department.id} className="bg-white border rounded-lg p-4 shadow" data-testid={`dept-card-${department.id}`}>
                    <h3 className="font-semibold text-lg mb-2">{department.name}</h3>
                    {department.description && (
                      <p className="text-gray-600 text-sm mb-2">{department.description}</p>
                    )}
                    <p className="text-xs text-gray-500">
                      Created: {new Date(department.created_at).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-gray-500 mb-3">
                      ID: {department.id}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t">
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(deptReviewUrl);
                          alert(`Department review link copied:\n${deptReviewUrl}`);
                        }}
                        data-testid={`dept-copy-link-${department.id}`}
                        className="text-xs bg-indigo-500 text-white px-3 py-1.5 rounded hover:bg-indigo-600"
                      >
                        Copy Review Link
                      </button>
                      <a
                        href={deptReviewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`dept-open-link-${department.id}`}
                        className="text-xs bg-gray-700 text-white px-3 py-1.5 rounded hover:bg-gray-800"
                      >
                        Open
                      </a>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          </ProtectedRoute>
        )}

        {activeTab === 'services' && user?.role === 'admin' && (
          <ProtectedRoute requireAdmin={true}>
            <ServicesTab departments={departments} publicBaseUrl={window.location.origin} />
          </ProtectedRoute>
        )}

        {activeTab === 'employees' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Employees</h2>
              <div className="space-x-2">
                <button
                  onClick={downloadCSVTemplate}
                  className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600"
                >
                  Download CSV Template
                </button>
                <button
                  onClick={downloadDepartmentUrls}
                  className="bg-purple-500 text-white px-4 py-2 rounded-md hover:bg-purple-600"
                  title="Download employee feedback URLs for selected departments"
                >
                  📋 Download Employee URLs
                </button>
                <label className="bg-orange-500 text-white px-4 py-2 rounded-md hover:bg-orange-600 cursor-pointer">
                  Bulk Upload CSV
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleBulkUpload}
                    className="hidden"
                  />
                </label>
                <button
                  onClick={() => setShowAddEmployee(true)}
                  className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
                >
                  Add Employee
                </button>
              </div>
            </div>
            
            {/* Department Selection for URL Download */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Select Departments for Employee URL Download:
              </h3>
              <div className="flex flex-wrap gap-2">
                {departments.map((dept) => (
                  <label key={dept.id} className="inline-flex items-center">
                    <input
                      type="checkbox"
                      className="form-checkbox h-4 w-4 text-blue-600"
                      checked={selectedDepartments.includes(dept.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDepartments([...selectedDepartments, dept.id]);
                        } else {
                          setSelectedDepartments(selectedDepartments.filter(id => id !== dept.id));
                        }
                      }}
                    />
                    <span className="ml-2 text-sm text-gray-700">{dept.name}</span>
                  </label>
                ))}
                <button
                  onClick={() => setSelectedDepartments(departments.map(d => d.id))}
                  className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded hover:bg-blue-200"
                >
                  Select All
                </button>
                <button
                  onClick={() => setSelectedDepartments([])}
                  className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded hover:bg-gray-200"
                >
                  Clear All
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {selectedDepartments.length === 0 
                  ? "No departments selected - will download all employees" 
                  : `Selected ${selectedDepartments.length} department(s)`}
              </p>
            </div>

            {/* Employee Filters and Sorting */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Filter & Sort Employees</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Search Filter */}
                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-1 ${getTextAlignment()}`}>
                    Search Name/ID
                  </label>
                  <input
                    type="text"
                    placeholder="Search employees..."
                    value={employeeFilters.search}
                    onChange={(e) => handleEmployeeFilterChange('search', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Department Filter */}
                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-1 ${getTextAlignment()}`}>
                    Filter by Department
                  </label>
                  <select
                    value={employeeFilters.department}
                    onChange={(e) => handleEmployeeFilterChange('department', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Departments</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.name}>{dept.name}</option>
                    ))}
                  </select>
                </div>

                {/* Position Filter */}
                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-1 ${getTextAlignment()}`}>
                    Filter by Position
                  </label>
                  <select
                    value={employeeFilters.position}
                    onChange={(e) => handleEmployeeFilterChange('position', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Positions</option>
                    {getUniquePositions().map((position) => (
                      <option key={position} value={position}>{position}</option>
                    ))}
                  </select>
                </div>

                {/* Sort Options */}
                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-1 ${getTextAlignment()}`}>
                    Sort by
                  </label>
                  <select
                    value={employeeSort.field}
                    onChange={(e) => handleEmployeeSortChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="name">Name</option>
                    <option value="department">Department</option>
                    <option value="position">Position</option>
                  </select>
                </div>
              </div>

              {/* Sort Order and Clear Filters */}
              <div className="flex justify-between items-center mt-4">
                <button
                  onClick={() => setEmployeeSort(prev => ({ ...prev, order: prev.order === 'asc' ? 'desc' : 'asc' }))}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Sort Order: {employeeSort.order === 'asc' ? '↑ Ascending' : '↓ Descending'}
                </button>
                
                <button
                  onClick={() => {
                    setEmployeeFilters({ department: '', position: '', search: '' });
                    setEmployeeSort({ field: 'name', order: 'asc' });
                  }}
                  className="text-sm text-gray-600 hover:text-gray-800 font-medium px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100"
                >
                  Clear Filters
                </button>
              </div>
              
              {/* Results Count */}
              <div className="mt-2 text-sm text-gray-600">
                Showing {getFilteredAndSortedEmployees().length} of {employees.length} employees
              </div>
            </div>

            {showAddEmployee && (
              <div className="mb-6 p-4 border rounded-lg bg-gray-50">
                <h3 className="text-lg font-medium mb-4">Add New Employee</h3>
                <form onSubmit={handleAddEmployee} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Name"
                    value={newEmployee.name}
                    onChange={(e) => setNewEmployee({...newEmployee, name: e.target.value})}
                    className="border rounded-md px-3 py-2"
                    required
                  />
                  <select
                    value={newEmployee.department_id}
                    onChange={(e) => setNewEmployee({...newEmployee, department_id: e.target.value})}
                    className="border rounded-md px-3 py-2"
                    required
                  >
                    <option value="">Select Department</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Position"
                    value={newEmployee.position}
                    onChange={(e) => setNewEmployee({...newEmployee, position: e.target.value})}
                    className="border rounded-md px-3 py-2"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Employee ID"
                    value={newEmployee.employee_id}
                    onChange={(e) => setNewEmployee({...newEmployee, employee_id: e.target.value})}
                    className="border rounded-md px-3 py-2"
                    required
                  />
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Employee Photo (optional)
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="border rounded-md px-3 py-2 w-full"
                    />
                    {selectedPhoto && (
                      <div className="mt-2">
                        <img
                          src={selectedPhoto}
                          alt="Preview"
                          className="w-20 h-20 object-cover rounded-full"
                        />
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-2 flex gap-2">
                    <button
                      type="submit"
                      className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600"
                    >
                      Add Employee
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddEmployee(false);
                        setSelectedPhoto(null);
                        setNewEmployee({ name: '', department_id: '', position: '', employee_id: '', photo: null });
                      }}
                      className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Edit Employee Form */}
            {showEditEmployee && (
              <div className="mb-6 p-4 border rounded-lg bg-yellow-50">
                <h3 className="text-lg font-medium mb-4">Edit Employee</h3>
                <form onSubmit={handleUpdateEmployee} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Name"
                    value={editingEmployee.name}
                    onChange={(e) => setEditingEmployee({...editingEmployee, name: e.target.value})}
                    className="border rounded-md px-3 py-2"
                    required
                  />
                  <select
                    value={editingEmployee.department_id}
                    onChange={(e) => setEditingEmployee({...editingEmployee, department_id: e.target.value})}
                    className="border rounded-md px-3 py-2"
                    required
                  >
                    <option value="">Select Department</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Position"
                    value={editingEmployee.position}
                    onChange={(e) => setEditingEmployee({...editingEmployee, position: e.target.value})}
                    className="border rounded-md px-3 py-2"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Employee ID"
                    value={editingEmployee.employee_id}
                    onChange={(e) => setEditingEmployee({...editingEmployee, employee_id: e.target.value})}
                    className="border rounded-md px-3 py-2"
                    required
                  />
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Employee Photo
                    </label>
                    
                    {/* Show current photo if exists */}
                    {editingEmployee.currentPhoto && !selectedPhoto && (
                      <div className="mb-3">
                        <p className="text-sm text-gray-600 mb-2">Current Photo:</p>
                        <img 
                          src={editingEmployee.currentPhoto} 
                          alt="Current employee photo" 
                          className="w-16 h-16 object-cover rounded-full border"
                        />
                      </div>
                    )}
                    
                    {/* Show new photo preview if selected */}
                    {selectedPhoto && (
                      <div className="mb-3">
                        <p className="text-sm text-gray-600 mb-2">New Photo Preview:</p>
                        <img 
                          src={selectedPhoto} 
                          alt="New employee photo" 
                          className="w-16 h-16 object-cover rounded-full border"
                        />
                      </div>
                    )}
                    
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (e) => {
                            const base64 = e.target.result;
                            setEditingEmployee({...editingEmployee, photo: base64});
                            setSelectedPhoto(base64);
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="border rounded-md px-3 py-2 w-full"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      {editingEmployee.currentPhoto 
                        ? "Select a new photo to replace the current one, or leave empty to keep current photo"
                        : "Select a photo for this employee"
                      }
                    </p>
                  </div>
                  <div className="md:col-span-2 flex space-x-4">
                    <button
                      type="submit"
                      className="bg-yellow-500 text-white px-4 py-2 rounded-md hover:bg-yellow-600"
                    >
                      Update Employee
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full table-auto">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Photo
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleEmployeeSortChange('name')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Name</span>
                        {employeeSort.field === 'name' && (
                          <span className="text-blue-600">
                            {employeeSort.order === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleEmployeeSortChange('department')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Department</span>
                        {employeeSort.field === 'department' && (
                          <span className="text-blue-600">
                            {employeeSort.order === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleEmployeeSortChange('position')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Position</span>
                        {employeeSort.field === 'position' && (
                          <span className="text-blue-600">
                            {employeeSort.order === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getFilteredAndSortedEmployees().map((employee) => (
                    <tr key={employee.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {employee.photo ? (
                          <img
                            src={employee.photo}
                            alt={employee.name}
                            className="w-10 h-10 object-cover rounded-full"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <span className="text-gray-500 text-xs">
                              {employee.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {employee.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {employee.department_name || employee.department}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {employee.position}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {employee.employee_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => testEmployeeLink(employee.id)}
                          className="text-green-600 hover:text-green-900 mr-3"
                          title="Test feedback form link"
                        >
                          Link
                        </button>
                        <button
                          onClick={() => generateQRCode(employee.id)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                          title="Generate QR code"
                        >
                          Generate QR
                        </button>
                        <button
                          onClick={() => generateEmployeeCard(employee)}
                          className="text-green-600 hover:text-green-900 mr-3"
                          title="Download employee ID card PDF"
                        >
                          Download Card
                        </button>
                        <button
                          onClick={() => {
                            const feedbackUrl = `https://system.ratemyservice.app/feedback/${employee.id}`;
                            navigator.clipboard.writeText(feedbackUrl);
                            alert('Feedback link copied to clipboard!');
                          }}
                          className="text-purple-600 hover:text-purple-900 mr-3"
                          title="Copy individual feedback link"
                        >
                          📋 Link
                        </button>
                        <button
                          onClick={() => handleEditEmployee(employee)}
                          className="text-yellow-600 hover:text-yellow-900 mr-3"
                          title="Edit employee details"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteEmployee(employee.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete employee"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {getFilteredAndSortedEmployees().length === 0 && (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center">
                        <div className="text-gray-500">
                          {employees.length === 0 ? (
                            <div>
                              <p className="text-lg mb-2">No employees found</p>
                              <p className="text-sm">Add your first employee to get started</p>
                            </div>
                          ) : (
                            <div>
                              <p className="text-lg mb-2">No employees match your filters</p>
                              <p className="text-sm">Try adjusting your filter criteria</p>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'feedbacks' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Feedbacks</h2>
              <button
                onClick={downloadFeedbackCSV}
                className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 text-sm"
              >
                Download CSV
              </button>
            </div>

            {/* Feedback Type Tabs */}
            <div className="bg-white rounded-lg shadow mb-6">
              <div className="border-b border-gray-200">
                <nav className="flex">
                  <button
                    onClick={() => setFeedbackFilter('employees')}
                    className={`px-6 py-3 text-sm font-medium ${
                      feedbackFilter === 'employees'
                        ? 'border-b-2 border-blue-500 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Employee Feedback
                  </button>
                  <button
                    onClick={() => setFeedbackFilter('organization')}
                    className={`px-6 py-3 text-sm font-medium ${
                      feedbackFilter === 'organization'
                        ? 'border-b-2 border-blue-500 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Organization Feedback
                  </button>
                </nav>
              </div>
            </div>

            {/* Employee Feedback Content */}
            {feedbackFilter === 'employees' && (
              <>
                {/* Feedback Filters */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Filter Feedbacks</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Customer Search */}
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 mb-1 ${getTextAlignment()}`}>
                        Search Customer
                      </label>
                      <input
                        type="text"
                        placeholder="Search by customer name..."
                        value={feedbackCustomerFilter}
                        onChange={(e) => setFeedbackCustomerFilter(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Department Filter */}
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 mb-1 ${getTextAlignment()}`}>
                        Filter by Department
                      </label>
                      <select
                        value={feedbackDepartmentFilter}
                        onChange={(e) => setFeedbackDepartmentFilter(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">All Departments</option>
                        {departments.map(dept => (
                          <option key={dept.id} value={dept.name}>{dept.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Rating Filter */}
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 mb-1 ${getTextAlignment()}`}>
                        Filter by Rating
                      </label>
                      <select
                        value={feedbackRatingFilter}
                        onChange={(e) => setFeedbackRatingFilter(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">All Ratings</option>
                        <option value="5">5 Stars - Excellent</option>
                        <option value="4">4 Stars - Very Good</option>
                        <option value="3">3 Stars - Good</option>
                        <option value="2">2 Stars - Bad</option>
                        <option value="1">1 Star - Worst</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    {/* From Date */}
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 mb-1 ${getTextAlignment()}`}>
                        From Date
                      </label>
                      <input
                        type="date"
                        value={feedbackFromDate}
                        onChange={(e) => setFeedbackFromDate(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* To Date */}
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 mb-1 ${getTextAlignment()}`}>
                        To Date
                      </label>
                      <input
                        type="date"
                        value={feedbackToDate}
                        onChange={(e) => setFeedbackToDate(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Quick Date Presets */}
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 mb-1 ${getTextAlignment()}`}>
                        Quick Filters
                      </label>
                      <select
                        onChange={(e) => setPresetDateRange(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                        value=""
                      >
                        <option value="">Select preset...</option>
                        <option value="today">Today</option>
                        <option value="yesterday">Yesterday</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                        <option value="quarter">This Quarter</option>
                      </select>
                    </div>
                  </div>

                  {/* Filter Actions and Results */}
                  <div className="flex justify-between items-center mt-4">
                    <div className="text-sm text-gray-600">
                      Showing {filteredFeedbacks.length} of {feedbacks.length} feedbacks
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={applyFeedbackFilters}
                        className="text-sm text-white bg-blue-600 hover:bg-blue-700 font-medium px-4 py-2 rounded-md"
                      >
                        Apply Filters
                      </button>
                      <button
                        onClick={handleClearFilters}
                        className="text-sm text-gray-600 hover:text-gray-800 font-medium px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100"
                      >
                        Clear All Filters
                      </button>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full table-auto">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Employee
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Department
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rating
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Comments
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredFeedbacks.map((feedback) => {
                        const employee = employees.find(emp => emp.id === feedback.employee_id);
                        return (
                          <tr key={feedback.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(feedback.created_at).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">{feedback.customer_name}</div>
                                {feedback.customer_phone && (
                                  <div className="text-sm text-gray-500">{feedback.customer_phone}</div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {employee?.name || 'Unknown'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {employee?.department_name || employee?.department || 'No Department'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${RATING_COLORS[feedback.rating]}`}>
                                {feedback.rating}/5 - {RATING_SCALE[feedback.rating]}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                              {feedback.comments || 'No comments'}
                            </td>
                          </tr>
                        );
                      })}
                      {filteredFeedbacks.length === 0 && (
                        <tr>
                          <td colSpan="6" className="px-6 py-8 text-center">
                            <div className="text-gray-500">
                              {feedbacks.length === 0 ? (
                                <div>
                                  <p className="text-lg mb-2">No feedback received yet</p>
                                  <p className="text-sm">Feedback will appear here as customers submit reviews</p>
                                </div>
                              ) : (
                                <div>
                                  <p className="text-lg mb-2">No feedback matches your filters</p>
                                  <p className="text-sm">Try adjusting your filter criteria or clearing all filters</p>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* Organization Feedback Content */}
            {feedbackFilter === 'organization' && (
              <>
                {/* Organization Feedback Filters */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Filter Organization Feedback</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Customer Search */}
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 mb-1 ${getTextAlignment()}`}>
                        Search Customer
                      </label>
                      <input
                        type="text"
                        placeholder="Search by customer name..."
                        value={feedbackCustomerFilter}
                        onChange={(e) => setFeedbackCustomerFilter(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Rating Filter */}
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 mb-1 ${getTextAlignment()}`}>
                        Filter by Rating
                      </label>
                      <select
                        value={feedbackRatingFilter}
                        onChange={(e) => setFeedbackRatingFilter(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">All Ratings</option>
                        <option value="5">5 Stars - Excellent</option>
                        <option value="4">4 Stars - Very Good</option>
                        <option value="3">3 Stars - Good</option>
                        <option value="2">2 Stars - Bad</option>
                        <option value="1">1 Star - Worst</option>
                      </select>
                    </div>

                    {/* Date Filter */}
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 mb-1 ${getTextAlignment()}`}>
                        From Date
                      </label>
                      <input
                        type="date"
                        value={feedbackFromDate}
                        onChange={(e) => setFeedbackFromDate(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="flex justify-between items-center mt-4">
                    <div className="text-sm text-gray-600">
                      Showing {organizationFeedbacks.length} organization feedback(s)
                    </div>
                    
                    <button
                      onClick={handleClearFilters}
                      className="text-sm text-gray-600 hover:text-gray-800 font-medium px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100"
                    >
                      Clear All Filters
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full table-auto">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Mobile Number
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rating
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Google Redirect
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Comments
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {organizationFeedbacks.map((feedback) => (
                        <tr key={feedback.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(feedback.submitted_at).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{feedback.customer_name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {feedback.mobile_number}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${RATING_COLORS[feedback.rating]}`}>
                              {feedback.rating}/5 - {RATING_SCALE[feedback.rating]}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              feedback.redirected_to_google ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {feedback.redirected_to_google ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                            {feedback.additional_comments || 'No comments'}
                          </td>
                        </tr>
                      ))}
                      {organizationFeedbacks.length === 0 && (
                        <tr>
                          <td colSpan="6" className="px-6 py-8 text-center">
                            <div className="text-gray-500">
                              <div>
                                <p className="text-lg mb-2">No organization feedback received yet</p>
                                <p className="text-sm">Organization feedback will appear here as customers submit reviews through the QR code</p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'configuration' && user?.role === 'admin' && (
          <ProtectedRoute requireAdmin={true}>
            <div>
              <h2 className="text-xl font-semibold mb-6">System Settings</h2>
              <p className="text-gray-600 mb-6">Manage and adjust the system settings for better performance or look.</p>
              
              {/* Settings Navigation Tabs */}
              <div className="bg-white rounded-lg shadow mb-6">
                <div className="border-b border-gray-200">
                  <nav className="flex">
                    {/* Organization-specific tabs (only for organization admins) */}
                    {user?.organization_id !== null && (
                      <>
                        <button
                          onClick={() => setSettingsTab('form')}
                          className={`px-6 py-3 text-sm font-medium ${
                            settingsTab === 'form'
                              ? 'border-b-2 border-blue-500 text-blue-600'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          Form Settings
                        </button>
                        <button
                          onClick={() => setSettingsTab('reviews')}
                          className={`px-6 py-3 text-sm font-medium ${
                            settingsTab === 'reviews'
                              ? 'border-b-2 border-blue-500 text-blue-600'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          Google Reviews
                        </button>
                        <button
                          onClick={() => setSettingsTab('geofencing')}
                          className={`px-6 py-3 text-sm font-medium ${
                            settingsTab === 'geofencing'
                              ? 'border-b-2 border-blue-500 text-blue-600'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          Geofencing
                        </button>
                      </>
                    )}
                    
                    {/* Global Settings Tab (Super Admin only) */}
                    {user?.role === 'admin' && user?.organization_id === null && (
                      <button
                        onClick={() => setSettingsTab('global')}
                        className={`px-6 py-3 text-sm font-medium ${
                          settingsTab === 'global'
                            ? 'border-b-2 border-blue-500 text-blue-600'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Global Settings
                      </button>
                    )}
                  </nav>
                </div>
              </div>
              
              {/* Organization-specific Configuration Content */}
              {systemSettings && user?.organization_id !== null && (
                <div className="space-y-6">
                  {/* Form Settings Tab */}
                  {settingsTab === 'form' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Review Form Settings */}
                        <div className="bg-gray-50 p-6 rounded-lg">
                          <h3 className="text-lg font-medium mb-4">Review Form Settings</h3>
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Ask reviewer to enter full name</label>
                              <select
                                value={systemSettings?.ask_full_name ? 'Yes' : 'No'}
                                onChange={(e) => updateSystemSettings({ask_full_name: e.target.value === 'Yes'})}
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                              >
                                <option value="Yes">Yes</option>
                                <option value="No">No</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Save Button */}
                      <div className="flex justify-end pt-6">
                        <button
                          onClick={() => alert('Form settings saved successfully!')}
                          className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                        >
                          Save Changes
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Other organization tabs... */}
                </div>
              )}

              {/* Organization Admin Settings Content */}
              {user?.role === 'admin' && user?.organization_id !== null && systemSettings && (
                <div className="space-y-6">
                  {settingsTab === 'general' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Review Form Settings */}
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <h3 className="text-lg font-medium mb-4">Review Form Settings</h3>
                        
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-gray-700">Ask reviewer to enter full name</label>
                            <select
                              value={systemSettings?.ask_full_name ? 'Yes' : 'No'}
                              onChange={(e) => updateSystemSettings({ask_full_name: e.target.value === 'Yes'})}
                              className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                            >
                              <option value="Yes">Yes</option>
                              <option value="No">No</option>
                            </select>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-gray-700">Ask reviewer to enter phone number</label>
                            <select
                              value={systemSettings?.ask_phone_number ? 'Yes' : 'No'}
                              onChange={(e) => updateSystemSettings({ask_phone_number: e.target.value === 'Yes'})}
                              className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                            >
                              <option value="Yes">Yes</option>
                              <option value="No">No</option>
                            </select>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-gray-700">Ask reviewer to write message</label>
                            <select
                              value={systemSettings.ask_comments ? 'Yes' : 'No'}
                              onChange={(e) => updateSystemSettings({ask_comments: e.target.value === 'Yes'})}
                              className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                            >
                              <option value="Yes">Yes</option>
                              <option value="No">No</option>
                            </select>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-gray-700">Form can be accessible from</label>
                            <select
                              value={systemSettings.form_accessible_from}
                              onChange={(e) => updateSystemSettings({form_accessible_from: e.target.value})}
                              className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                            >
                              <option value="everywhere">Everywhere</option>
                              <option value="geofenced">Geofenced Areas Only</option>
                            </select>
                          </div>

                          {/* Language Settings */}
                          <div className="border-t pt-4">
                            <h4 className="text-sm font-semibold text-gray-800 mb-3">Language Settings</h4>
                            
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <label className="text-sm font-medium text-gray-700">Supported Languages</label>
                                <select
                                  value={systemSettings?.supported_languages?.length === 2 ? 'Both' : systemSettings?.supported_languages?.[0] || 'English'}
                                  onChange={(e) => {
                                    let supportedLanguages = [];
                                    let defaultLanguage = 'English';
                                    
                                    if (e.target.value === 'Both') {
                                      supportedLanguages = ['English', 'Arabic'];
                                      defaultLanguage = 'English';
                                    } else if (e.target.value === 'Arabic') {
                                      supportedLanguages = ['Arabic'];
                                      defaultLanguage = 'Arabic';
                                    } else {
                                      supportedLanguages = ['English'];
                                      defaultLanguage = 'English';
                                    }
                                    
                                    updateSystemSettings({
                                      supported_languages: supportedLanguages,
                                      default_language: defaultLanguage
                                    });
                                  }}
                                  className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                                >
                                  <option value="English">English Only</option>
                                  <option value="Arabic">Arabic Only</option>
                                  <option value="Both">English & Arabic</option>
                                </select>
                              </div>
                              
                              {systemSettings?.supported_languages?.length > 1 && (
                                <div className="flex items-center justify-between">
                                  <label className="text-sm font-medium text-gray-700">Default Language</label>
                                  <select
                                    value={systemSettings?.default_language || 'English'}
                                    onChange={(e) => updateSystemSettings({default_language: e.target.value})}
                                    className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                                  >
                                    {systemSettings.supported_languages.map(lang => (
                                      <option key={lang} value={lang}>{lang}</option>
                                    ))}
                                  </select>
                                </div>
                              )}
                              
                              <div className="flex items-center justify-between">
                                <label className="text-sm font-medium text-gray-700">Auto-detect Language</label>
                                <select
                                  value={systemSettings?.auto_detect_language ? 'Yes' : 'No'}
                                  onChange={(e) => updateSystemSettings({auto_detect_language: e.target.value === 'Yes'})}
                                  className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                                >
                                  <option value="No">No</option>
                                  <option value="Yes">Yes</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Notification Settings */}
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <h3 className="text-lg font-medium mb-4">Notification Settings</h3>
                        <p className="text-xs text-gray-500 mb-4">
                          Get an email alert whenever a customer leaves a low rating —
                          covers employee feedback, department reviews, and custom service evaluations.
                        </p>
                        
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-gray-700">Bad review threshold (less or equal than)</label>
                            <select
                              value={systemSettings.bad_review_threshold}
                              onChange={(e) => updateSystemSettings({bad_review_threshold: parseInt(e.target.value)})}
                              className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                            >
                              <option value="1">1</option>
                              <option value="2">2</option>
                              <option value="3">3</option>
                              <option value="4">4</option>
                            </select>
                          </div>
                          
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Notify admin for bad reviews to the following email address:
                            </label>
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={systemSettings.notify_admin_bad_reviews}
                                onChange={(e) => updateSystemSettings({notify_admin_bad_reviews: e.target.checked})}
                                className="rounded border-gray-300"
                              />
                              <span className="text-sm text-gray-600">Enable notifications</span>
                            </div>
                            <input
                              type="email"
                              placeholder="admin@company.com"
                              value={systemSettings.admin_notification_emails?.join(', ') || ''}
                              onChange={(e) => {
                                const emails = e.target.value.split(',').map(email => email.trim()).filter(Boolean);
                                updateSystemSettings({admin_notification_emails: emails});
                              }}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Google Reviews Tab */}
                  {settingsTab === 'reviews' && (
                    <div className="max-w-2xl">
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <h3 className="text-lg font-medium mb-4">Google Reviews Integration</h3>
                        
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Google Reviews URL
                            </label>
                            <input
                              type="url"
                              value={systemSettings.google_review_url || ''}
                              onChange={(e) => updateSystemSettings({google_review_url: e.target.value})}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                              placeholder="https://g.page/r/your-business-id/review"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Customers with 4-5 star ratings will be redirected to this URL
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Save Button */}
                      <div className="flex justify-end pt-6">
                        <button
                          onClick={() => alert('Google Reviews settings saved successfully!')}
                          className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                        >
                          Save Changes
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Geofencing Tab */}
                  {settingsTab === 'geofencing' && (
                    <div>
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <h3 className="text-lg font-medium mb-4">Geofencing Settings</h3>
                        
                        <div className="space-y-4">
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={systemSettings.geofence_enabled}
                              onChange={(e) => updateSystemSettings({geofence_enabled: e.target.checked})}
                              className="rounded border-gray-300"
                            />
                            <label className="text-sm font-medium text-gray-700">
                              Enable geofencing (restrict form access to specific areas)
                            </label>
                          </div>
                          
                          {systemSettings.geofence_enabled && (
                            <div>
                              <GeofenceMap 
                                polygons={systemSettings.geofence_polygons || []}
                                onPolygonsChange={(polygons) => updateSystemSettings({geofence_polygons: polygons})}
                                isDrawing={isDrawing}
                                setIsDrawing={setIsDrawing}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                  {/* Daily Review Limits */}
                  <div className="border-t pt-4">
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">Daily Review Limits</h4>
                        
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-gray-700">Enable Daily Review Limits</label>
                            <select
                              value={systemSettings?.daily_review_limits_enabled ? 'Yes' : 'No'}
                              onChange={(e) => updateSystemSettings({daily_review_limits_enabled: e.target.value === 'Yes'})}
                              className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                            >
                              <option value="No">No</option>
                              <option value="Yes">Yes</option>
                            </select>
                          </div>
                          
                          {systemSettings?.daily_review_limits_enabled && (
                            <>
                              <div className="flex items-center justify-between">
                                <label className="text-sm font-medium text-gray-700">Daily Limit per Employee</label>
                                <input
                                  type="number"
                                  min="1"
                                  max="100"
                                  value={systemSettings?.daily_review_limit_per_employee || 10}
                                  onChange={(e) => updateSystemSettings({daily_review_limit_per_employee: parseInt(e.target.value)})}
                                  className="border border-gray-300 rounded-md px-3 py-1 text-sm w-20"
                                />
                              </div>
                              
                              <div className="flex items-center justify-between">
                                <label className="text-sm font-medium text-gray-700">Limit Type</label>
                                <select
                                  value={systemSettings?.daily_review_limit_type || 'warning'}
                                  onChange={(e) => updateSystemSettings({daily_review_limit_type: e.target.value})}
                                  className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                                >
                                  <option value="warning">Warning (Allow submission)</option>
                                  <option value="hard_block">Hard Block (Prevent submission)</option>
                                </select>
                              </div>
                              
                              <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">Limit Message</label>
                                <textarea
                                  value={systemSettings?.daily_review_limit_message || "This employee has reached the daily feedback limit. Please try again tomorrow."}
                                  onChange={(e) => updateSystemSettings({daily_review_limit_message: e.target.value})}
                                  rows="2"
                                  className="w-full border border-gray-300 rounded-md px-3 py-1 text-sm"
                                  placeholder="Message shown when limit is reached..."
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Website Redirection Settings */}
                      <div className="border-t pt-4">
                        <h4 className="text-sm font-semibold text-gray-800 mb-3">Website Redirection</h4>
                        
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-gray-700">Enable Website Redirection</label>
                            <select
                              value={systemSettings?.enable_website_redirection ? 'Yes' : 'No'}
                              onChange={(e) => updateSystemSettings({enable_website_redirection: e.target.value === 'Yes'})}
                              className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                            >
                              <option value="Yes">Yes</option>
                              <option value="No">No</option>
                            </select>
                          </div>
                          
                          {systemSettings?.enable_website_redirection && (
                            <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-md">
                              <p>When enabled, customers will be redirected to your organization's website after submitting feedback.</p>
                              <p className="mt-1"><strong>Note:</strong> Make sure your organization has a website URL configured.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              )}

              {/* Super Admin Global Settings Content */}
              {user?.role === 'admin' && user?.organization_id === null && (
                <div className="space-y-6">
                  {/* Global Settings Tab (Super Admin only) */}
                  {settingsTab === 'global' && (
                    <div>
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <h3 className="text-lg font-medium mb-4">Global System Settings</h3>
                        <p className="text-sm text-gray-600 mb-6">Configure global settings that affect all tenants and the login page</p>
                        
                        <div className="space-y-6">
                          {/* Login Page Configuration */}
                          <div className="border-b pb-6">
                            <h4 className="text-md font-medium mb-4">Login Page Configuration</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                                  Login Page Title
                                </label>
                                <input
                                  type="text"
                                  value={globalSettings.login_page_title || ''}
                                  onChange={(e) => updateGlobalSettings({login_page_title: e.target.value})}
                                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                                  placeholder="Rate My Service"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                                  Login Page Subtitle
                                </label>
                                <input
                                  type="text"
                                  value={globalSettings.login_page_subtitle || ''}
                                  onChange={(e) => updateGlobalSettings({login_page_subtitle: e.target.value})}
                                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                                  placeholder="Customer Feedback Management Platform"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                                  Super Admin Login Text
                                </label>
                                <input
                                  type="text"
                                  value={globalSettings.super_admin_login_text || ''}
                                  onChange={(e) => updateGlobalSettings({super_admin_login_text: e.target.value})}
                                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                                  placeholder="System Administration Access"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                                  Tenant Login Text
                                </label>
                                <input
                                  type="text"
                                  value={globalSettings.tenant_login_text || ''}
                                  onChange={(e) => updateGlobalSettings({tenant_login_text: e.target.value})}
                                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                                  placeholder="Sign in to your organization's dashboard"
                                />
                              </div>
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                                  Tenant Login Help Text
                                </label>
                                <textarea
                                  value={globalSettings.tenant_login_help_text || ''}
                                  onChange={(e) => updateGlobalSettings({tenant_login_help_text: e.target.value})}
                                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                                  rows="2"
                                  placeholder="Enter your organization slug, email, and password to access your dashboard"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Global Logo Configuration */}
                          <div className="border-b pb-6">
                            <h4 className="text-md font-medium mb-4">Global Logo Configuration</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                                  Global Logo
                                </label>
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleGlobalLogoUpload}
                                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  This logo will appear on the login page for all users
                                </p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                                  Logo Position
                                </label>
                                <select
                                  value={globalSettings.logo_position || 'center'}
                                  onChange={(e) => updateGlobalSettings({logo_position: e.target.value})}
                                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                                >
                                  <option value="left">Left</option>
                                  <option value="center">Center</option>
                                  <option value="right">Right</option>
                                </select>
                              </div>
                              {globalSettings.logo && (
                                <div className="md:col-span-2">
                                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                                    Current Logo Preview
                                  </label>
                                  <div className={`flex ${
                                    globalSettings.logo_position === 'left' ? 'justify-start' : 
                                    globalSettings.logo_position === 'right' ? 'justify-end' : 'justify-center'
                                  } p-4 border border-gray-200 rounded-md bg-white`}>
                                    <img
                                      src={globalSettings.logo}
                                      alt="Global Logo"
                                      className="h-16 w-auto object-contain"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* System Branding */}
                          <div>
                            <h4 className="text-md font-medium mb-4">System Branding</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                                  Company Name
                                </label>
                                <input
                                  type="text"
                                  value={globalSettings.company_name || ''}
                                  onChange={(e) => updateGlobalSettings({company_name: e.target.value})}
                                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                                  placeholder="Rate My Service"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                                  Support Email
                                </label>
                                <input
                                  type="email"
                                  value={globalSettings.support_email || ''}
                                  onChange={(e) => updateGlobalSettings({support_email: e.target.value})}
                                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                                  placeholder="support@ratemyservice.com"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Save Button */}
                      <div className="flex justify-end pt-6 border-t border-gray-200">
                        <button
                          onClick={() => {
                            alert('Settings saved successfully!');
                          }}
                          className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                        >
                          Save Changes
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </ProtectedRoute>
        )}

        {/* My Subscription Tab (Organization Admin Only) */}
        {activeTab === 'my-subscription' && user?.role === 'admin' && user?.organization_id !== null && (
          <div>
            <h2 className="text-xl font-semibold mb-6 text-left">My Subscription</h2>
            <p className="text-gray-600 mb-6 text-left">View your current subscription details and request changes.</p>
            
            {/* Subscription Overview Card */}
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 text-left">Current Plan</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Package:</span>
                      <span className="font-medium">Loading...</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                        Active Trial
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Trial Ends:</span>
                      <span className="font-medium">Loading...</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Monthly Price:</span>
                      <span className="font-medium text-blue-600">Loading...</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 text-left">Usage</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Staff Members:</span>
                      <span className="font-medium">0 / Loading...</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">This Month's Reviews:</span>
                      <span className="font-medium">0</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Request Package Change */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 text-left">Request Package Change</h3>
              <p className="text-gray-600 mb-4 text-left">
                Want to upgrade or downgrade your subscription? Changes take effect at the end of your current billing period.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Select New Package *
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    defaultValue=""
                    onChange={(e) => {
                      // Store selected package for submission
                      const selectedPackage = e.target.value;
                      console.log('Selected package:', selectedPackage);
                    }}
                  >
                    <option value="">Choose a package...</option>
                    <option value="self-employed">Self-Employed - 100 AED/month (1 staff)</option>
                    <option value="start-up">Start-Up - 500 AED/month (2-10 staff)</option>
                    <option value="corporate">Corporate - 1500 AED/month (10-50 staff)</option>
                    <option value="enterprise">Enterprise - 5000 AED/month (Unlimited staff)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Billing Cycle
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    defaultValue="monthly"
                  >
                    <option value="monthly">Monthly Billing</option>
                    <option value="annual">Annual Billing (Save with yearly payment)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Reason for Change (Optional)
                  </label>
                  <textarea
                    rows="3"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Please provide a reason for the package change (e.g., business growth, downsizing, need more features)..."
                  />
                </div>
                
                <div className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    id="understand-billing"
                    className="mt-1"
                  />
                  <label htmlFor="understand-billing" className="text-sm text-gray-600">
                    I understand that this change will take effect at the end of my current billing period and requires Super Admin approval.
                  </label>
                </div>
                
                <button
                  onClick={() => {
                    // TODO: Implement package change request submission
                    alert('Package change request will be submitted to Super Admin for approval!\n\nYour request will be reviewed and you will receive an email confirmation once processed.');
                  }}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors font-medium"
                >
                  Submit Package Change Request
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Packages Tab (Super Admin Only) */}
        {activeTab === 'subscription-packages' && user?.role === 'admin' && user?.organization_id === null && (
          <ProtectedRoute requireAdmin={true}>
            <div>
              <h2 className="text-xl font-semibold mb-6 text-left">Subscription Packages</h2>
              <p className="text-gray-600 mb-6 text-left">Manage subscription packages and pricing for all organizations.</p>
              
              {/* Packages List */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900 text-left">Current Packages</h3>
                    <button
                      onClick={() => {
                        // TODO: Implement add package modal
                        alert('Add package feature coming soon!');
                      }}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                    >
                      Add Package
                    </button>
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Package Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Staff Limit
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Monthly Price
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Annual Price
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr>
                        <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                          Loading packages...
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </ProtectedRoute>
        )}

        {/* Package Change Requests Tab (Super Admin Only) */}
        {activeTab === 'package-change-requests' && user?.role === 'admin' && user?.organization_id === null && (
          <ProtectedRoute requireAdmin={true}>
            <div>
              <h2 className="text-xl font-semibold mb-6 text-left">Package Change Requests</h2>
              <p className="text-gray-600 mb-6 text-left">Review and approve package change requests from organizations.</p>
              
              {/* Requests List */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 text-left">Pending Requests</h3>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Organization
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Current Package
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Requested Package
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Request Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr>
                        <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                          No pending requests
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </ProtectedRoute>
        )}

        {/* Organization Tab (Organization Admin Only) */}
        {activeTab === 'organization' && user?.role === 'admin' && user?.organization_id !== null && (
          <div>
            <h2 className="text-xl font-semibold mb-6 text-left">Organization</h2>
            <p className="text-gray-600 mb-6 text-left">Manage your organization settings and evaluation system.</p>

            {/* Tab Navigation */}
            <div className="bg-white rounded-lg shadow mb-6">
              <div className="border-b border-gray-200">
                <nav className="flex">
                  <button
                    onClick={() => setOrganizationTab('account')}
                    className={`px-6 py-3 text-sm font-medium ${
                      organizationTab === 'account'
                        ? 'border-b-2 border-blue-500 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Account & Security
                  </button>
                  <button
                    onClick={() => setOrganizationTab('logo')}
                    className={`px-6 py-3 text-sm font-medium ${
                      organizationTab === 'logo'
                        ? 'border-b-2 border-blue-500 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Logo & Branding
                  </button>
                  <button
                    onClick={() => setOrganizationTab('evaluation')}
                    className={`px-6 py-3 text-sm font-medium ${
                      organizationTab === 'evaluation'
                        ? 'border-b-2 border-blue-500 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Organization Evaluation
                  </button>
                  <button
                    onClick={() => setOrganizationTab('feedback-settings')}
                    className={`px-6 py-3 text-sm font-medium ${
                      organizationTab === 'feedback-settings'
                        ? 'border-b-2 border-blue-500 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Feedback Settings
                  </button>
                </nav>
              </div>
            </div>

            {/* Account & Security Tab */}
            {organizationTab === 'account' && (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-medium mb-4 text-left">Account & Security</h3>
                <p className="text-sm text-gray-600 mb-6 text-left">Manage your account settings and security preferences</p>
                
                <div className="space-y-6">
                  {/* Account Information */}
                  <div className="border-b pb-6">
                    <h4 className="text-md font-medium mb-4 text-left">Account Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Name</label>
                        <p className="text-sm text-gray-900 bg-gray-100 px-3 py-2 rounded-md">{user?.name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Email</label>
                        <p className="text-sm text-gray-900 bg-gray-100 px-3 py-2 rounded-md">{user?.email}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Role</label>
                        <p className="text-sm text-gray-900 bg-gray-100 px-3 py-2 rounded-md">{user?.role}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Last Login</label>
                        <p className="text-sm text-gray-900 bg-gray-100 px-3 py-2 rounded-md">
                          {user?.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Password Change */}
                  <div>
                    <h4 className="text-md font-medium mb-4 text-left">Change Password</h4>
                    <div className="max-w-md space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Current Password</label>
                        <input
                          type="password"
                          value={passwordForm.currentPassword}
                          onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter your current password"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 text-left">New Password</label>
                        <input
                          type="password"
                          value={passwordForm.newPassword}
                          onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter new password"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Confirm New Password</label>
                        <input
                          type="password"
                          value={passwordForm.confirmPassword}
                          onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                          placeholder="Confirm new password"
                        />
                      </div>
                      <button
                        onClick={handlePasswordChange}
                        disabled={
                          !passwordForm.currentPassword || 
                          !passwordForm.newPassword || 
                          !passwordForm.confirmPassword ||
                          passwordForm.newPassword !== passwordForm.confirmPassword ||
                          passwordForm.newPassword.length < 8
                        }
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Change Password
                      </button>
                      {passwordForm.newPassword !== passwordForm.confirmPassword && passwordForm.confirmPassword && (
                        <p className="text-sm text-red-600">Passwords do not match</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Logo & Branding Tab */}
            {organizationTab === 'logo' && (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-medium mb-4 text-left">Logo & Branding</h3>
                <p className="text-sm text-gray-600 mb-6 text-left">
                  Manage your organization's logo and branding settings
                </p>
                
                <div className="space-y-6">
                  {/* Organization Logo Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3 text-left">
                      Organization Logo
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                      {systemSettings?.logo ? (
                        <div className="text-center">
                          <img
                            src={systemSettings.logo}
                            alt="Organization Logo"
                            className="h-24 object-contain mx-auto mb-4"
                          />
                          <p className="text-sm text-gray-600 mb-3">Current logo</p>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleOrganizationLogoUpload}
                            className="hidden"
                            id="logo-upload"
                          />
                          <label
                            htmlFor="logo-upload"
                            className="cursor-pointer bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors text-sm"
                          >
                            Change Logo
                          </label>
                        </div>
                      ) : (
                        <div className="text-center">
                          <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <div className="mt-4">
                            <label htmlFor="logo-upload" className="cursor-pointer">
                              <span className="mt-2 block text-sm font-medium text-gray-900">
                                Upload organization logo
                              </span>
                              <span className="mt-1 block text-sm text-gray-500">
                                PNG, JPG, GIF up to 5MB
                              </span>
                            </label>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleOrganizationLogoUpload}
                              className="hidden"
                              id="logo-upload"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Logo Display Settings */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3 text-left">
                      Logo Display Settings
                    </label>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-gray-700 text-left">Show logo on feedback forms</label>
                        <select
                          value={systemSettings?.show_logo_on_feedback_form !== false ? 'Yes' : 'No'}
                          onChange={(e) => updateSystemSettings({show_logo_on_feedback_form: e.target.value === 'Yes'})}
                          className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                        >
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      </div>
                      
                      {systemSettings?.show_logo_on_feedback_form !== false && (
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-gray-700 text-left">Logo position on forms</label>
                          <select
                            value={systemSettings?.logo_position || 'top-left'}
                            onChange={(e) => updateSystemSettings({logo_position: e.target.value})}
                            className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                          >
                            <option value="top-left">Top Left</option>
                            <option value="top-center">Top Center</option>
                            <option value="top-right">Top Right</option>
                          </select>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Organization Information */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3 text-left">
                      Organization Information
                    </label>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Organization Name</label>
                        <input
                          type="text"
                          value={user?.organization_name || ''}
                          disabled
                          className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-500"
                          placeholder="Organization name cannot be changed here"
                        />
                        <p className="text-xs text-gray-500 mt-1 text-left">
                          To change the organization name, contact support
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Website URL</label>
                        <input
                          type="url"
                          value={systemSettings?.organization_website || ''}
                          onChange={(e) => updateSystemSettings({organization_website: e.target.value})}
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="https://your-organization.com"
                        />
                        <p className="text-xs text-gray-500 mt-1 text-left">
                          Customers will be redirected here after feedback submission
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Save Button */}
                <div className="flex justify-end pt-6 border-t border-gray-200">
                  <button
                    onClick={() => alert('Logo & branding settings saved successfully!')}
                    className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            )}

            {/* Organization Evaluation Tab */}
            {organizationTab === 'evaluation' && (
              <OrganizationEvaluationTab 
                user={user} 
                organizationId={user?.organization_id}
                setMessage={null}
              />
            )}

            {/* Feedback Settings Tab */}
            {organizationTab === 'feedback-settings' && (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-medium mb-4 text-left">Low Rating Feedback Settings</h3>
                <p className="text-sm text-gray-600 mb-6 text-left">
                  Customize the feedback form shown to customers who give 1-3 star ratings
                </p>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3 text-left">
                      Feedback Categories (Radio Button Options)
                    </label>
                    <div className="space-y-4">
                      {['Service Quality', 'Staff Behavior', 'Facility Cleanliness', 'Overall Experience'].map((category, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-gray-700 text-left">Category {index + 1}</label>
                          </div>
                          <input
                            type="text"
                            defaultValue={category}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 mb-2"
                            placeholder="Category name"
                          />
                          <textarea
                            rows="2"
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                            placeholder="Options (one per line): Excellent, Good, Fair, Poor"
                            defaultValue="Excellent\nGood\nFair\nPoor"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      Additional Comments Section
                    </label>
                    <input
                      type="text"
                      defaultValue="Tell us more about your experience"
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="Text box label"
                    />
                  </div>
                  
                  <button
                    onClick={() => {
                      // TODO: Save feedback settings
                      alert('Feedback settings saved successfully!');
                    }}
                    className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Save Settings
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tenant Details Modal */}
        {showTenantDetailsModal && selectedTenant && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 mx-4 max-w-4xl w-full max-h-96 overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-800">Tenant Details - {selectedTenant.name}</h3>
                <button
                  onClick={() => {
                    setShowTenantDetailsModal(false);
                    setSelectedTenant(null);
                    setTenantDetails(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Current Organization Info */}
                <div className="space-y-4">
                  <h4 className="text-md font-semibold text-gray-900 border-b pb-2">Current Organization Info</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Name:</span> {selectedTenant.name}</p>
                    <p><span className="font-medium">Slug:</span> {selectedTenant.slug}</p>
                    <p><span className="font-medium">Contact Email:</span> {selectedTenant.contact_email || 'N/A'}</p>
                    <p><span className="font-medium">Phone:</span> {selectedTenant.phone || 'N/A'}</p>
                    <p><span className="font-medium">Industry:</span> {selectedTenant.industry || 'N/A'}</p>
                    <p><span className="font-medium">Website:</span> 
                      {selectedTenant.website ? (
                        <a href={selectedTenant.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 ml-1">
                          {selectedTenant.website}
                        </a>
                      ) : 'N/A'}
                    </p>
                    <p><span className="font-medium">Status:</span> 
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ml-2 ${
                        selectedTenant.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedTenant.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </p>
                    <p><span className="font-medium">Created:</span> {new Date(selectedTenant.created_at).toLocaleString()}</p>
                    <p><span className="font-medium">Last Updated:</span> {selectedTenant.updated_at ? new Date(selectedTenant.updated_at).toLocaleString() : 'N/A'}</p>
                  </div>
                  
                  {/* Evaluation Statistics */}
                  <div className="mt-6">
                    <h5 className="text-md font-semibold text-gray-900 border-b pb-2">Evaluation Statistics</h5>
                    <div className="mt-2 p-4 bg-blue-50 rounded-lg">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">{tenantFeedbackCount}</div>
                        <div className="text-sm text-gray-600">Total Evaluations</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Original Registration Details */}
                <div className="space-y-4">
                  <h4 className="text-md font-semibold text-gray-900 border-b pb-2">Original Registration Details</h4>
                  {tenantDetails ? (
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">Organization Name:</span> {tenantDetails.organization_name}</p>
                      <p><span className="font-medium">Industry:</span> {tenantDetails.industry}</p>
                      <p><span className="font-medium">Team Size:</span> {tenantDetails.team_size}</p>
                      <p><span className="font-medium">Location:</span> {tenantDetails.location}</p>
                      <p><span className="font-medium">Official Email:</span> {tenantDetails.official_email}</p>
                      <p><span className="font-medium">Phone Number:</span> {tenantDetails.phone_number}</p>
                      <p><span className="font-medium">Contact Person:</span> {tenantDetails.contact_person_name}</p>
                      <p><span className="font-medium">Contact Title:</span> {tenantDetails.contact_person_title}</p>
                      <p><span className="font-medium">Expected Reviews:</span> {tenantDetails.expected_monthly_reviews}</p>
                      <p><span className="font-medium">Preferred Language:</span> {tenantDetails.preferred_language}</p>
                      <p><span className="font-medium">Card Requirement:</span> {tenantDetails.card_requirement}</p>
                      {tenantDetails.company_registration_number && (
                        <p><span className="font-medium">Registration #:</span> {tenantDetails.company_registration_number}</p>
                      )}
                      {tenantDetails.special_requirements && (
                        <div className="mt-3">
                          <p className="font-medium">Special Requirements:</p>
                          <p className="text-gray-600 bg-gray-50 p-2 rounded mt-1">{tenantDetails.special_requirements}</p>
                        </div>
                      )}
                      
                      {/* Registration Timeline */}
                      <div className="mt-4 pt-4 border-t">
                        <h5 className="font-medium mb-2">Registration Timeline</h5>
                        <p className="text-xs text-gray-500">
                          <span className="font-medium">Submitted:</span> {new Date(tenantDetails.submitted_at).toLocaleString()}
                        </p>
                        {tenantDetails.reviewed_at && (
                          <p className="text-xs text-gray-500">
                            <span className="font-medium">Reviewed:</span> {new Date(tenantDetails.reviewed_at).toLocaleString()}
                          </p>
                        )}
                        {tenantDetails.approval_notes && (
                          <div className="mt-2 p-2 bg-green-50 rounded">
                            <p className="text-xs font-medium text-green-700">Approval Notes:</p>
                            <p className="text-xs text-green-600">{tenantDetails.approval_notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="text-sm text-gray-500 mt-2">Loading registration details...</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        </div>
        </div>
      </div>
    </div>
  );
};
// Feedback Form Component
const FeedbackForm = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [systemSettings, setSystemSettings] = useState(null);
  const [locationAllowed, setLocationAllowed] = useState(true);
  const [checkingLocation, setCheckingLocation] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState('English');
  const [translations, setTranslations] = useState(null);
  const [dailyLimitStatus, setDailyLimitStatus] = useState(null);
  const [showLimitWarning, setShowLimitWarning] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    rating: 0,
    comments: ''
  });

  useEffect(() => {
    fetchEmployee();
    // No longer need to fetch system settings separately as they come with employee data
  }, [employeeId]);

  useEffect(() => {
    // Check daily limits after employee is loaded
    if (employee && systemSettings) {
      checkDailyLimits();
    }
  }, [employee, systemSettings]);

  useEffect(() => {
    // Only check geofencing if it's explicitly enabled and settings are loaded
    if (systemSettings?.geofence_enabled === true) {
      checkGeofencePermission();
    } else {
      // Skip geofencing check to speed up loading
      setLocationAllowed(true);
      setCheckingLocation(false);
    }
  }, [systemSettings]);

  useEffect(() => {
    // Load translations when language changes
    if (currentLanguage) {
      fetchTranslations(currentLanguage);
    }
  }, [currentLanguage]);

  useEffect(() => {
    // Set initial language based on system settings
    if (systemSettings && systemSettings.supported_languages) {
      if (systemSettings.auto_detect_language) {
        // Auto-detect language from browser
        const browserLang = navigator.language.toLowerCase();
        if (browserLang.includes('ar') && systemSettings.supported_languages.includes('Arabic')) {
          setCurrentLanguage('Arabic');
        } else if (systemSettings.supported_languages.includes('English')) {
          setCurrentLanguage('English');
        } else {
          setCurrentLanguage(systemSettings.supported_languages[0]);
        }
      } else {
        setCurrentLanguage(systemSettings.default_language || 'English');
      }
    }
  }, [systemSettings]);

  const fetchTranslations = async (language) => {
    try {
      const response = await axios.get(`${API}/public/translations/${language}`);
      setTranslations(response.data);
    } catch (error) {
      console.error('Error fetching translations:', error);
      // Fallback to English if translation fails
      if (language !== 'English') {
        setCurrentLanguage('English');
      }
    }
  };

  const fetchEmployee = async () => {
    try {
      const response = await axios.get(`${API}/public/employee/${employeeId}`);
      setEmployee(response.data);
      
      // Use form_settings from employee response instead of separate settings call
      if (response.data.form_settings) {
        setSystemSettings(response.data.form_settings);
        
        // Set language based on form settings
        if (response.data.form_settings.default_language) {
          setCurrentLanguage(response.data.form_settings.default_language);
        }
      }
    } catch (error) {
      console.error('Error fetching employee:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemSettings = async () => {
    try {
      const response = await axios.get(`${API}/public/settings`);
      setSystemSettings(response.data);
    } catch (error) {
      console.error('Error fetching system settings:', error);
      // Set default settings if fetch fails
      setSystemSettings({
        ask_full_name: true,
        ask_phone_number: true,
        ask_comments: true,
        geofence_enabled: false
      });
    }
  };

  const checkDailyLimits = async () => {
    try {
      const response = await axios.get(`${API}/public/employee/${employeeId}/daily-limit-status`);
      setDailyLimitStatus(response.data);
      
      // Show warning if limits are enabled and close to being reached
      if (response.data.limits_enabled && response.data.current_count >= response.data.daily_limit * 0.8) {
        setShowLimitWarning(true);
      }
    } catch (error) {
      console.error('Error checking daily limits:', error);
      // Continue without limit checking if it fails
      setDailyLimitStatus(null);
    }
  };

  const checkGeofencePermission = async () => {
    setCheckingLocation(true);
    
    try {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            setUserLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            });
            await validateLocation(position.coords.latitude, position.coords.longitude);
          },
          (error) => {
            console.error('Geolocation error:', error);
            // Continue without location if user denies or error occurs
            setCheckingLocation(false);
          }
        );
      } else {
        setCheckingLocation(false);
      }
    } catch (error) {
      console.error('Error checking geofence:', error);
      setCheckingLocation(false);
    }
  };

  const validateLocation = async (latitude, longitude) => {
    try {
      const params = userLocation ? `?latitude=${userLocation.latitude}&longitude=${userLocation.longitude}` : '';
      const response = await axios.post(`${API}/settings/validate-location${params}`, {
        latitude,
        longitude
      });
      setLocationAllowed(response.data.allowed);
    } catch (error) {
      console.error('Error validating location:', error);
      setLocationAllowed(false);
    } finally {
      setCheckingLocation(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.rating === 0) {
      // Show error message instead of alert
      setSuccessMessage(translations?.rating_required || 'Please select a rating before submitting.');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      return;
    }

    setSubmitting(true);

    try {
      const submitData = {
        employee_id: employeeId,
        customer_name: formData.customer_name,
        customer_phone: formData.customer_phone,
        rating: formData.rating,
        rating_text: translations?.ratings ? translations.ratings[formData.rating] : RATING_SCALE[formData.rating],
        comments: formData.comments
      };

      const response = await axios.post(`${API}/public/feedback`, submitData);
      
      if (response.data) {
        // Check if there's a limit warning in the response
        if (response.data.limit_warning) {
          setSuccessMessage(response.data.limit_warning.message + ` (${response.data.limit_warning.current_count}/${response.data.limit_warning.daily_limit})`);
          setShowSuccessMessage(true);
          setTimeout(() => setShowSuccessMessage(false), 4000);
        }

        // Show success message
        setSuccessMessage(translations?.thank_you || 'Thank you for your feedback!');
        setShowSuccessMessage(true);

        // Check if we should redirect to Google Reviews
        if (formData.rating >= 4 && systemSettings?.google_review_url) {
          setIsRedirecting(true);
          setTimeout(() => {
            window.location.href = systemSettings.google_review_url;
          }, 2000);
        } else {
          // Check if website redirection is enabled and organization has website
          if (systemSettings?.enable_website_redirection && systemSettings?.organization_website) {
            setIsRedirecting(true);
            setTimeout(() => {
              window.location.href = systemSettings.organization_website;
            }, 2000);
          } else {
            // Fallback to success page if no website redirection
            setTimeout(() => {
              navigate(`/feedback-success/${employeeId}`);
            }, 2000);
          }
        }
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      
      // Handle daily limit hard block (429 status)
      if (error.response && error.response.status === 429) {
        const errorData = error.response.data;
        if (errorData && errorData.detail && typeof errorData.detail === 'object') {
          setSuccessMessage(`Daily limit reached: ${errorData.detail.message} (${errorData.detail.current_count}/${errorData.detail.daily_limit})`);
        } else {
          setSuccessMessage(translations?.daily_limit_reached || 'Daily feedback limit has been reached for this employee. Please try again tomorrow.');
        }
      } else {
        setSuccessMessage(translations?.error_submitting || 'Error submitting feedback. Please try again.');
      }
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 4000);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{translations?.loading || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (checkingLocation) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{translations?.checking_location || 'Checking your location...'}</p>
        </div>
      </div>
    );
  }

  if (!locationAllowed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-red-500 text-6xl mb-4">📍</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{translations?.location_restricted || 'Location Restricted'}</h1>
          <p className="text-gray-600">{translations?.location_restricted_message || 'This feedback form can only be accessed from authorized locations.'}</p>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{translations?.employee_not_found || 'Employee Not Found'}</h1>
          <p className="text-gray-600">{translations?.employee_not_found_message || "The employee you're looking for doesn't exist."}</p>
        </div>
      </div>
    );
  }

  // Determine text direction for RTL languages like Arabic
  const isRTL = currentLanguage === 'Arabic';
  const directionClass = isRTL ? 'rtl' : 'ltr';
  const textAlignClass = isRTL ? 'text-right' : 'text-left';

  return (
    <div className={`min-h-screen bg-gray-50 py-8 ${directionClass}`}>
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8">
        {/* Language Selector */}
        {systemSettings?.supported_languages && systemSettings.supported_languages.length > 1 && (
          <div className="mb-4 flex justify-end">
            <select 
              value={currentLanguage}
              onChange={(e) => setCurrentLanguage(e.target.value)}
              className="text-sm border border-gray-300 rounded px-2 py-1"
            >
              {systemSettings.supported_languages.map(lang => (
                <option key={lang} value={lang}>
                  {SUPPORTED_LANGUAGES[lang] || lang}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Logo Section */}
        {systemSettings?.logo && systemSettings?.show_logo_on_feedback_form !== false && (
          <div className={`mb-6 ${
            systemSettings.logo_position === 'top-center' ? 'text-center' :
            systemSettings.logo_position === 'top-right' ? 'text-right' : 'text-left'
          }`}>
            <img
              src={systemSettings.logo}
              alt="Company Logo"
              className="h-12 object-contain inline-block"
            />
          </div>
        )}
        
        <div className="text-center mb-8">
          <div className="mb-4">
            {employee.photo ? (
              <img
                src={employee.photo}
                alt={employee.name}
                className="w-28 h-28 object-cover rounded-full mx-auto"
                style={{ width: '110px', height: '110px' }}
              />
            ) : (
              <div className="w-28 h-28 bg-gray-200 rounded-full mx-auto flex items-center justify-center"
                   style={{ width: '110px', height: '110px' }}>
                <span className="text-gray-500 text-2xl">
                  {employee.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </span>
              </div>
            )}
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{employee.name}</h1>
          <p className="text-gray-600">{employee.position}</p>
          <p className="text-sm text-gray-500">{employee.department_name}</p>
        </div>

        {/* Daily Limit Warning Banner */}
        {dailyLimitStatus && dailyLimitStatus.limits_enabled && (
          <div className={`rounded-md p-4 mb-4 ${
            dailyLimitStatus.limit_reached 
              ? 'bg-red-50 border border-red-200' 
              : dailyLimitStatus.current_count >= dailyLimitStatus.daily_limit * 0.8 
                ? 'bg-yellow-50 border border-yellow-200'
                : 'bg-blue-50 border border-blue-200'
          }`}>
            <div className="flex">
              <div className="flex-shrink-0">
                {dailyLimitStatus.limit_reached ? (
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                ) : dailyLimitStatus.current_count >= dailyLimitStatus.daily_limit * 0.8 ? (
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  dailyLimitStatus.limit_reached 
                    ? 'text-red-800' 
                    : dailyLimitStatus.current_count >= dailyLimitStatus.daily_limit * 0.8 
                      ? 'text-yellow-800'
                      : 'text-blue-800'
                }`}>
                  {dailyLimitStatus.limit_reached 
                    ? translations?.daily_limit_reached || dailyLimitStatus.message || 'Daily feedback limit reached'
                    : `${dailyLimitStatus.current_count}/${dailyLimitStatus.daily_limit} daily reviews submitted`
                  }
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {showSuccessMessage && (
          <div className={`rounded-md p-4 mb-4 ${
            isRedirecting 
              ? 'bg-green-50 border border-green-200' 
              : successMessage.includes('error') || successMessage.includes('limit') || successMessage.includes('required')
                ? 'bg-red-50 border border-red-200'
                : 'bg-green-50 border border-green-200'
          }`}>
            <div className="flex">
              <div className="flex-shrink-0">
                {isRedirecting ? (
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : successMessage.includes('error') || successMessage.includes('limit') || successMessage.includes('required') ? (
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  successMessage.includes('error') || successMessage.includes('limit') || successMessage.includes('required')
                    ? 'text-red-800' 
                    : 'text-green-800'
                }`}>
                  {successMessage}
                </p>
                {isRedirecting && (
                  <p className="text-sm text-green-600 mt-1">
                    {translations?.redirecting || 'Redirecting in a moment...'}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6"
          style={{opacity: dailyLimitStatus?.limit_reached || showSuccessMessage ? 0.6 : 1}}
        >
          {systemSettings?.ask_full_name && (
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${textAlignClass}`}>
                {translations?.your_name || 'Your Name'}
              </label>
              <input
                type="text"
                value={formData.customer_name}
                onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                className={`w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${textAlignClass}`}
                required
              />
            </div>
          )}

          {systemSettings?.ask_phone_number && (
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${textAlignClass}`}>
                {translations?.your_phone || 'Your Phone Number'}
              </label>
              <input
                type="tel"
                value={formData.customer_phone}
                onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                className={`w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${textAlignClass}`}
                required
              />
            </div>
          )}

          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-4 ${textAlignClass}`}>
              {translations?.rating_question || 'How would you rate this service?'}
            </label>
            <div className="flex justify-center space-x-2">
              {[1, 2, 3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  type="button"
                  onClick={() => setFormData({...formData, rating})}
                  className={`w-12 h-12 rounded-full border-2 flex items-center justify-center font-bold transition-all ${
                    formData.rating === rating
                      ? `${RATING_COLORS[rating]} border-transparent transform scale-110 text-white`
                      : 'border-gray-300 bg-gray-100 text-black hover:bg-gray-200'
                  }`}
                >
                  {rating}
                </button>
              ))}
            </div>
            <div className="text-xs text-gray-500 font-medium text-center mt-2">
              1=worst - 5=excellent
            </div>
            {formData.rating > 0 && (
              <p className={`text-center text-sm text-gray-600 mt-2`}>
                {translations?.ratings ? translations.ratings[formData.rating] : RATING_SCALE[formData.rating]}
              </p>
            )}
          </div>

          {/* Always show feedback text field - make it optional */}
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-2 ${textAlignClass}`}>
              {translations?.additional_comments || 'Additional Comments'} <span className="text-gray-400">
                {translations?.optional || '(Optional)'}
              </span>
            </label>
            <textarea
              value={formData.comments}
              onChange={(e) => setFormData({...formData, comments: e.target.value})}
              rows="4"
              className={`w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${textAlignClass}`}
              placeholder={translations?.comments_placeholder || "Tell us about your experience..."}
            />
          </div>

          <button
            type="submit"
            disabled={submitting || formData.rating === 0 || (dailyLimitStatus?.limit_reached && dailyLimitStatus?.limit_type === 'hard_block')}
            className={`w-full py-3 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
              dailyLimitStatus?.limit_reached && dailyLimitStatus?.limit_type === 'hard_block'
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'
            }`}
          >
            {submitting 
              ? (translations?.submitting || 'Submitting...') 
              : dailyLimitStatus?.limit_reached && dailyLimitStatus?.limit_type === 'hard_block'
                ? (translations?.limit_reached || 'Daily Limit Reached')
                : (translations?.submit_button || 'Submit Feedback')
            }
          </button>
        </form>
      </div>
    </div>
  );
};

// Success Page Component
const FeedbackSuccess = () => {
  const { employeeId } = useParams();
  const [employee, setEmployee] = useState(null);
  const [systemSettings, setSystemSettings] = useState(null);

  useEffect(() => {
    fetchEmployee();
    fetchSystemSettings();
  }, [employeeId]);

  const fetchEmployee = async () => {
    try {
      const response = await axios.get(`${API}/public/employees/${employeeId}`);
      setEmployee(response.data);
    } catch (error) {
      console.error('Error fetching employee:', error);
    }
  };

  const fetchSystemSettings = async () => {
    try {
      const response = await axios.get(`${API}/public/settings`);
      setSystemSettings(response.data);
    } catch (error) {
      console.error('Error fetching system settings:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="text-green-500 text-6xl mb-4">✅</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Thank You!</h1>
        <p className="text-gray-600 mb-6">
          Your feedback has been submitted successfully.
        </p>
        
        {employee && (
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <p className="text-sm text-gray-600">
              Feedback submitted for <strong>{employee.name}</strong>
            </p>
            <p className="text-sm text-gray-500">
              {employee.position} - {employee.department_name}
            </p>
          </div>
        )}
        
        <p className="text-sm text-gray-500">
          We appreciate your time and feedback. It helps us improve our service.
        </p>
      </div>
    </div>
  );
};

// Main App Component
const App = () => {
  return (
    <AuthProvider>
      <MainApp />
    </AuthProvider>
  );
};

const MainApp = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <div className="App">
        <Routes>
          {/* Public Routes */}
          <Route path="/feedback/:employeeId" element={<FeedbackForm />} />
          <Route path="/feedback-success/:employeeId" element={<FeedbackSuccess />} />
          <Route path="/organization-feedback/:organizationId" element={<OrganizationFeedback />} />
          <Route path="/organization-feedback-details/:organizationId" element={<OrganizationFeedbackDetails />} />
          <Route path="/organization-feedback-success/:organizationId" element={<OrganizationFeedbackSuccess />} />
          <Route path="/department-feedback/:departmentId" element={<DepartmentFeedback />} />
          <Route path="/service-feedback/:serviceId" element={<ServiceEvaluation />} />
          <Route path="/connection-test" element={<ConnectionTest />} />
          <Route path="/super-admin" element={<SuperAdminLogin />} />
          <Route path="/admin" element={
            <ProtectedRoute>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } /> {/* Super Admin dashboard route */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Home and Dashboard Routes */}
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              ) : (
                <MarketingLayout />
              )
            }
          >
            <Route index element={<MarketingHome />} />
          </Route>

          {/* Public marketing pages (always accessible) */}
          <Route element={<MarketingLayout />}>
            <Route path="/about" element={<About />} />
            <Route path="/packages" element={<Packages />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<RegistrationForm />} />
          </Route>
          
          {/* Dashboard Route for authenticated users */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } />
        </Routes>
      </div>
    </BrowserRouter>
  );
};

export default App;/* Build timestamp: Sat Aug  9 06:48:02 UTC 2025 */
